import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import { PermButton } from "@/lib/rbac";
import StatusBadge from "@/components/StatusBadge";
import CancelWithReasonDialog from "@/components/CancelWithReasonDialog";
import AdminAmendDialog from "@/components/AdminAmendDialog";
import HoldReasonDialog from "@/components/HoldReasonDialog";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { PlusIcon, TrashIcon, PencilIcon, DownloadSimpleIcon, CheckCircleIcon, LockIcon, LinkIcon, ClockCounterClockwiseIcon, EyeIcon, PrinterIcon, CaretUpDownIcon, CheckIcon, MagnifyingGlassIcon, FileTextIcon, StackIcon, ProhibitIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { useLatestRowFlash } from "@/lib/latestRow";
import { getUser } from "@/lib/secureStorage";
import { canSubmitDoc } from "@/lib/submitPerms";
import ApprovalControls from "@/components/ApprovalControls";
import { fmtDateTimeIST, fmtDateIST } from "@/lib/datetime";
import DocumentInfo from "@/components/DocumentInfo";
import { DocDownloadButtons, downloadDoc } from "@/components/DocDownloadActions";
import SearchablePicker from "@/components/SearchablePicker";
import { useAuth } from "@/lib/auth";
import { openTracePdf } from "@/lib/traceExport";
import { printDocument } from "@/lib/print";
// Feb 2026 · Shared print entry point — SO / PI / PO all route their
// browser-print action through this so the visual template stays in
// perfect sync across the three documents.
import { printDocument as printDocumentHtml } from "@/components/DocumentPrintTemplate";
import PackingSlipsTab from "@/pages/PackingSlipsTab";
import { PackingListsTab, DeliveryChallansTab } from "@/pages/OrderExtras";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { baseArticle } from "@/lib/article";
import { useSearchParams } from "react-router-dom";

import FormattedNumberInput from "@/components/FormattedNumberInput";
import LineHistoryDrawer from "@/components/LineHistoryDrawer";
import LineQtyActions from "@/components/LineQtyActions";
import { LineQtyBreakdown, ProtectionBadge } from "@/components/LineQtyBreakdown";
import { PROTECTION_TEXT, canViewQty, isActionablePending, isOrderCancelled, isSubmittedPendingApproval, useQtyPositions } from "@/lib/qtyDisplay";
import ModifyDocumentPopover from "@/components/ModifyDocumentPopover";

// Feb 2026 · Qty display helper — Order / Total / Dispatched / Pending Qty
// render as comma-grouped integers (e.g. 1000 → "1,000") everywhere they
// appear as read-only cells (grids, previews, footers, print meta).
// Feb 2026 · Unit-aware formatters — the single source of truth lives in
// `lib/uom`. SO line-items are meters (no decimals, Indian commas) and
// every rate/value cell is INR (2 dp, Indian commas, ₹ prefix).
import { fmtQty as _fmtQty, fmtInr as _fmtInr } from "@/lib/uom";
import { FULFILMENT_LABEL, FulfilmentChoice, StockBadge, fulfilmentOf } from "@/components/StockBadge";
const fmtQtyInt = (v) => _fmtQty(v ?? 0, "MTR");
const fmtInr = (v) => _fmtInr(v);
const inp = "w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const inpNum = inp + " text-right font-mono";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] h-8 text-sm";

const emptyLine = (idx = 0) => ({
  item_no: String(idx + 1).padStart(3, "0"),
  item_id: "", customer_po_no: "", style_no: "", article_no: "", article_id: "",
  description: "", colour: "", size: "",
  order_qty: 0, extra_qty: 0, extra_pct: 0, disp_qty: 0, rate: 0, unit: "meter",
});

export default function SalesOrders() {
  const { hasPerm } = useAuth();
  const [orders, setOrders] = useState([]);
  // P2 · Feb 2026 · Latest-row flash — new order ids get a 3s emerald
  // ring so operators immediately spot their freshly-saved row after a
  // list re-fetch. Baseline pass on first render skips highlighting the
  // whole seeded list.
  const flashingOrderIds = useLatestRowFlash(orders, "id");
  const [woSummary, setWoSummary] = useState({});
  // Feb 2026 · Commercial Integrity — field-level red highlights on save.
  const [fieldErrors, setFieldErrors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [articles, setArticles] = useState([]);
  // Feb 2026 · Downstream-link lock map (order_id → "Packing Slip PS-26-037"
  // etc.). Used to badge/hide Edit + Delete on the grid so users never
  // stumble into a 409 when an SO is already downstream. Fetched once with
  // the rest of the SO data.
  const [downstreamLocks, setDownstreamLocks] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // SOESO-DN01 · C-1/C-2 — one in-flight save at a time; one stable Idempotency-Key per genuine Create.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const createKeyRef = useRef(null);
  const [isAmending, setIsAmending] = useState(false);
  const [amendReason, setAmendReason] = useState("");
  const [historyOrder, setHistoryOrder] = useState(null);
  const [adminAmend, setAdminAmend] = useState(null);
  const [holdAction, setHoldAction] = useState(null); // {id, mode:'hold'|'release'}
  const [preview, setPreview] = useState(null);
  // IMP-1C · backend capability model for an unauthorised SO in View (legacy Short Close / Reverse host).
  const [previewCtl, setPreviewCtl] = useState(null);
  useEffect(() => {
    setPreviewCtl(null);
    if (!preview?.id || preview.authorized) return undefined;
    let live = true;
    api.get(`/sales-orders/${preview.id}`).then((r) => { if (live) setPreviewCtl(r.data?.controls || null); }).catch(() => {});
    return () => { live = false; };
  }, [preview?.id, preview?.authorized]);
  // Feb 28, 2026 · Per-line audit drawer (edits · short-closes · excess dispatches).
  const [historyLine, setHistoryLine] = useState(null);
  // Feb 2026 · Dynamic Order Modification — opens the shared popover that
  // link-probes downstream docs and branches into Unapprove OR Short Close /
  // Cancel Balance depending on what's linked to this SO.
  // Feb 27, 2026 · `modifyTarget` unlocks the popover from the preview
  // modal so the row-level Modify button opens it directly.
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyTarget, setModifyTarget] = useState(null);
  // PHASE-2 · First-class Cancel Order for an authorised SO (distinct from
  // the Draft delete/withdraw flow). Drives CancelWithReasonDialog →
  // POST /sales-orders/{id}/cancel.
  const [cancelOrderTarget, setCancelOrderTarget] = useState(null);
  const user = getUser();
  // Aug 31, 2026 · Zero-Trust — swap the hardcoded role-set for a
  // DB-backed permission check. Any admin-minted role holding
  // `sales_orders:approve` can authorise; Administrator bypasses.
  // `hasPerm` already destructured from `useAuth()` at the top of the
  // component (line 54) — reuse it here instead of re-declaring.
  const canAuthorize = user?.role === "Administrator" || !!hasPerm?.("sales_orders:approve");
  // Feb 07, 2026 · Management-only Approve/Unapprove split — `canEdit`
  // covers everything the PermissionReferenceGuide defines as the
  // `edit` verb: amend, modify qty on a line, short-close. Approve /
  // Unapprove stay on `canAuthorize`. Administrator bypasses both.
  const canEdit = user?.role === "Administrator" || !!hasPerm?.("sales_orders:edit");
  // IMP-1C · P-2 — action-specific Modify Order permissions (backend: sales_orders:cancel / :unapprove).
  const canSoClose = user?.role === "Administrator" || !!hasPerm?.("sales_orders:cancel");
  const canSoUnapprove = user?.role === "Administrator" || !!hasPerm?.("sales_orders:unapprove");
  // R2-01L A4-02 · D2 · read-only quantity breakdown for existing SO viewers.
  const canQtyView = canViewQty(user, hasPerm, "so");
  const [qpos] = useQtyPositions("so", preview?.id, !!preview?.authorized && canQtyView);

  // Sales Orders grid — column layout per user spec:
  // Order No · Date · Customer · Customer PO · Item No · Style No · Article No · HS · Description · Colour · Size · Order Qty · then delivery/status/actions.
  // Mar 3, 2026 · Column order tightened — Customer + Customer PO stay
  // adjacent; Item → Style → Product Code kept as a triplet everywhere.
  const COL_ORDER = ["order_no", "sales_date", "customer", "po", "item_no", "style_no", "article_no", "hs_code", "description", "colour", "size", "order_qty", "extra_qty", "total_qty", "disp_qty", "pending_qty", "rate", "value", "delivery", "wo_status", "status", "actions"];
  // Mar 1, 2026 · Column visibility for the Orders grid — mirrors the
  // Status Report toggle. Order No + Actions are pinned. Rate/Value
  // hidden by default (opt-in for finance-sensitive views). Persisted
  // per user in localStorage.
  const SO_PINNED_COLS = new Set(["order_no", "actions"]);
  // SO Register UI/UX Pilot V1 · CORRECTION 02 — line-wise register default
  // columns (operational set). Secondary/finance fields stay in the Columns
  // chooser (nothing deleted, personalization preserved).
  const SO_DEFAULT_HIDDEN = new Set(["style_no", "hs_code", "description", "colour", "size", "rate", "value", "priority"]);
  const [soVisibleCols, setSoVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.so_orders.cols");
      if (raw) return new Set(JSON.parse(raw));
    } catch (e) { /* ignore */ }
    return new Set(COL_ORDER.filter(k => !SO_DEFAULT_HIDDEN.has(k)));
  });
  const [soShowColsMenu, setSoShowColsMenu] = useState(false);
  const toggleSoCol = (k) => setSoVisibleCols(prev => {
    if (SO_PINNED_COLS.has(k)) return prev;   // pinned columns cannot be hidden
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.so_orders.cols", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const soResetCols = () => {
    const next = new Set(COL_ORDER.filter(k => !SO_DEFAULT_HIDDEN.has(k)));
    setSoVisibleCols(next);
    try { localStorage.setItem("mes.so_orders.cols", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
  };
  // Mar 3, 2026 · Density toggle — Comfortable (default px-4 py-3) vs
  // Compact (px-2 py-1.5). Persisted per user so buyers scanning long
  // backlogs don't reset their preferred density on every page load.
  const [soDensity, setSoDensity] = useState(() => {
    try { return localStorage.getItem("mes.so_orders.density") || "comfortable"; }
    catch { return "comfortable"; }
  });
  const setDensity = (d) => {
    setSoDensity(d);
    try { localStorage.setItem("mes.so_orders.density", d); } catch (e) { /* ignore */ }
  };
  const cellPad = soDensity === "compact" ? "px-2 py-1.5" : "px-4 py-3";
  // Mar 3, 2026 · Column Pin Menu — users can pin any columns in the
  // chooser (📌). Product Code is baseline-pinned via `COLS.article_no.sticky`
  // so it can never be un-pinned; extra pins stack to its LEFT with
  // cumulative `left` offsets computed from an approximate 160px/column.
  const [soPinnedKeys, setSoPinnedKeys] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.so_orders.pinned");
      if (raw) return new Set(JSON.parse(raw));
    } catch (e) { /* ignore */ }
    return new Set();
  });
  const toggleSoPin = (k) => setSoPinnedKeys(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.so_orders.pinned", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const soVisCols = COL_ORDER.filter(k => SO_PINNED_COLS.has(k) || soVisibleCols.has(k));
  // Mar 3, 2026 · Compute the ordered list of pinned columns (baseline sticky
  // ones + user-pinned) and a left-offset map so multi-pin cells stack cleanly.
  // Placed AFTER soVisCols so the pinned-column ordering respects visibility.
  const stickyOrder = soVisCols.filter(k => COLS[k]?.sticky || soPinnedKeys.has(k));
  const stickyLefts = {};
  stickyOrder.forEach((k, i) => { stickyLefts[k] = i * 160; });
  // Feb 2026 · Deep-link sub-tab via ?tab= query param so the sidebar's
  // Packing Slips / Packing Lists / Delivery Challans menu items land
  // directly on the right tab. Two-way binding — clicking a tab updates
  // the URL, and pasting a link opens the tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return ["orders", "slips", "packing", "challans", "report"].includes(t) ? t : "orders";
  })();
  const [subTab, setSubTabInternal] = useState(initialTab);
  const setSubTab = (t) => {
    setSubTabInternal(t);
    // Keep the URL in sync without pushing a new history entry.
    const next = new URLSearchParams(searchParams);
    if (t === "orders") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  // Sync tab if URL changes via back/forward or sidebar re-click.
  useEffect(() => {
    const t = searchParams.get("tab") || "orders";
    if (["orders", "slips", "packing", "challans", "report"].includes(t) && t !== subTab) {
      setSubTabInternal(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [orderSearch, setOrderSearch] = useState("");

  // One-click article filter — applied to Orders immediately, and stashed in sessionStorage
  // for the Packing Lists tab to auto-consume on its next mount.
  const setArticleFilter = (articleNo) => {
    if (!articleNo) return;
    setOrderSearch(articleNo);
    setSubTab("orders");
    try { sessionStorage.setItem("mes_pl_article_filter", articleNo); }
    catch (e) { console.warn("sessionStorage unavailable for article filter handoff", e); }
    toast.success(`Filtered by article "${articleNo}" — open Packing Lists to see linked PLs`);
  };

  // Workflow "next step" nudges — after saving an SO/Slip/PL/DC we surface a
  // toast with an action button that switches to the next sub-tab and prefills
  // the picked Sales Order via sessionStorage (each target tab picks it up on
  // mount / when its `orders` list is loaded).
  const NEXT_STEP_KEYS = { slips: "mes_ps_next_order", packing: "mes_pl_next_order", challans: "mes_dc_next_order", report: "mes_report_next_order" };
  const goToTab = (tabKey, orderId) => {
    if (orderId && NEXT_STEP_KEYS[tabKey]) {
      try { sessionStorage.setItem(NEXT_STEP_KEYS[tabKey], orderId); }
      catch (e) { console.warn("sessionStorage unavailable for next-step handoff", e); }
    }
    setSubTab(tabKey);
  };

  // Bulk-from-SO shortcut (Feb 2026). Per-line action on the Orders grid —
  // bundles the SO + article + style into sessionStorage so the Packing
  // Slips tab picks it up on mount and auto-opens the Bulk dialog with
  // everything pre-filled. Operator only fills box count + weights.
  const openBulkForLine = (o, line) => {
    if (!line) return;
    const payload = {
      order_id: o.id || "",
      customer_id: o.customer_id || "",
      product_code: line.article_no || "",
      item_id: line.article_id || line.item_id || "",
      style_no: line.style_no || "",
    };
    try { sessionStorage.setItem("mes_ps_next_bulk", JSON.stringify(payload)); }
    catch (e) { console.warn("sessionStorage unavailable for bulk handoff", e); }
    setSubTab("slips");
    toast.info(`Opening Bulk Slips for ${line.article_no || "line"} · ${o.order_no}`);
  };

  const empty = {
    order_no: "",
    // Feb 2026 · Order-against tab (Customer PO vs Stock). Stock orders skip
    // Customer + PO No entirely; only line items are required.
    order_against: "customer_po",
    fulfilment_mode: "production_sales",
    customer_id: "",
    // Feb 2026 · per-order Bill To / Ship To pointer into the customer's
    // saved addresses list. Empty string means "use customer's primary".
    bill_to_address_id: "",
    ship_to_address_id: "",
    // Feb 2026 · Address SNAPSHOTS — the fully-resolved bill/ship address
    // as it stood when the SO was created / last edited. Persisting the
    // snapshot means later edits to the Customer Master can't retroactively
    // rewrite what was shipped. Also serves as the mutable one-time
    // override buffer when the user hand-edits the address fields.
    bill_to_snapshot: {},
    ship_to_snapshot: {},
    po_number: "",
    order_date: new Date().toISOString().slice(0, 10),
    delivery_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    priority: "Normal",
    status: "Open",
    special_instructions: "",
    items: [emptyLine(0)],
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    // Sep 03, 2026 · Every fetch is wrapped in `.catch()` so a per-endpoint
    // 403 (e.g. a Packing-Dept user has no `sales_orders:view` grant but
    // does have `sales_orders_slips:view` for the sub-tabs) never rips down
    // the whole Promise.all → runtime overlay. Missing data simply renders
    // as an empty picker.
    const [o, c, it, ar, dl, wos] = await Promise.all([
      api.get("/sales-orders/").catch(() => ({ data: [] })),
      api.get("/customers/").catch(() => ({ data: [] })),
      api.get("/items/").catch(() => ({ data: [] })),
      api.get("/articles/").catch(() => ({ data: [] })),
      api.get("/sales-orders/downstream-locks").catch(() => ({ data: { locks: {} } })),
      api.get("/work-orders/summary/by-source").catch(() => ({ data: {} })),
    ]);
    setOrders(dedupById(o.data));
    setCustomers(c.data);
    setItems(it.data);
    // Feb 2026 · Smart Lock — hide retired FG SKUs from new SO article
    // pickers. Historical SO lines that reference inactive articles still
    // load fine because we filter only the picker source, not stored data.
    setArticles((ar.data || []).filter(a => a.is_active !== false));
    setDownstreamLocks(dl?.data?.locks || {});
    setWoSummary(wos.data || {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = async () => {
    setEditing(null);
    createKeyRef.current = uuidv4();
    // Feb 2026 · SO No is now assigned at SAVE-time, not at dialog-open, so
    // two operators drafting simultaneously never collide on the same
    // `SO-YY-YY-NNNNN`. The dialog shows an "Auto-assigned on Save" tag; the
    // actual number is fetched right before the POST.
    const firstLine = emptyLine(0);
    firstLine.delivery_date = empty.delivery_date;
    setForm({ ...empty, order_no: "", items: [firstLine] });
    setOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setIsAmending(!!o.authorized);
    setAmendReason("");
    const norm = (o.items?.length ? o.items : [emptyLine(0)]).map((li, i) => ({
      ...emptyLine(i),
      ...li,
      item_no: String(i + 1).padStart(3, "0"),
      order_qty: li.order_qty ?? li.quantity ?? 0,
      disp_qty: li.disp_qty ?? li.produced ?? 0,
      // Rule 1 back-fill (Feb 2026) — old orders saved before this rule may
      // have blank line-level dates. Seed them from the SO's headline date
      // so amending the SO stays consistent with the new "lines match SO"
      // invariant.
      delivery_date: li.delivery_date || o.delivery_date || "",
    }));
    // Rule 2 sync — pull the top-level date up to the latest line date so
    // amend dialogs open with an already-correct headline.
    const dates = norm.map(li => li.delivery_date).filter(Boolean);
    const topDelivery = dates.length
      ? dates.reduce((a, b) => (a > b ? a : b))
      : o.delivery_date;
    setForm({ ...empty, ...o, items: norm, delivery_date: topDelivery });
    setOpen(true);
  };

  const authorize = async (o) => {
    if (!canAuthorize) { toast.error("Your role cannot authorize"); return; }
    if (!confirm(`Authorise ${o.order_no}? Once authorised, edits are only allowed via amendment.`)) return;
    try {
      await api.post(`/sales-orders/${o.id}/authorize`, { remarks: "" });
      toast.success(`${o.order_no} authorised`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authorize failed");
    }
  };

  // P1-SO-MANAGEMENT-AUTH-CONTROL · Phase 2B · UAT-02 · Feb 2026 —
  // Positive amendment-Draft detection. An SO whose `approval_status`
  // is "Draft" is considered submit-eligible ONLY when its
  // `approval_history` proves the Draft came from a controlled
  // unapprove/amendment revert (entry with `action="Draft"` +
  // `prev_status="Authorized"`). Bare / provenance-less Draft SOs are
  // NOT eligible — parity with the backend guard.
  const isAmendmentDraft = (o) => {
    if ((o?.approval_status || "").trim() !== "Draft") return false;
    const hist = o?.approval_history || [];
    return hist.some(
      (e) => e?.action === "Draft" && e?.prev_status === "Authorized",
    );
  };

  // P1-SO-MANAGEMENT-AUTH-CONTROL · Phase 2B · Feb 2026 — MA-INV-02.
  // Explicit Submit / Resubmit action. Accepts Not Submitted / Rejected;
  // never called on already-Authorised or Pending SOs (frontend gates
  // the button to the right lifecycle state). UAT-02 extension: also
  // callable on amendment-Draft SOs (see `isAmendmentDraft`).
  const submitForApproval = async (o) => {
    const appr = (o.approval_status || "").trim();
    const isResubmit = appr === "Rejected";
    const isReauth = isAmendmentDraft(o);
    const verb = isReauth ? "Submit Amendment" : (isResubmit ? "Resubmit" : "Submit");
    const question = isReauth
      ? `Submit amended ${o.order_no} for Management Re-authorisation?`
      : `${verb} ${o.order_no} for Management Authorisation?`;
    if (!confirm(question)) return;
    try {
      await api.post(`/sales-orders/${o.id}/submit-for-approval`);
      toast.success(`${o.order_no} ${isReauth ? "submitted for re-authorisation" : isResubmit ? "resubmitted" : "submitted"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    }
  };

  const total_qty = (l) => {
    const order = Number(l.order_qty) || 0;
    const extra = Number(l.extra_qty) || 0;
    if (order || extra) return order + extra;
    return Number(l.quantity) || 0; // legacy fallback
  };
  const pending_qty = (l) => {
    // Sep 04, 2026 · Single-source pending — mirrors backend
    // `lib_pending.get_line_pending`: max(0, order − cancelled − disp).
    // Buffer (Extra Qty) is a TECHNICAL over-dispatch cap and is never
    // counted in the pending gauge — a line that has shipped its full
    // order_qty drops to 0 pending regardless of the buffer allowance.
    if (l.short_closed) return 0;
    const order = Number(l.order_qty ?? l.quantity) || 0;
    const cancelled = Number(l.cancelled_qty) || 0;
    const disp = Number(l.disp_qty ?? l.produced) || 0;
    return Math.max(0, order - cancelled - disp);
  };
  const value_of = (l) => total_qty(l) * (Number(l.rate) || 0);

  const orderTotals = (items = []) => {
    const q = items.reduce((a, l) => a + total_qty(l), 0);
    const d = items.reduce((a, l) => a + (Number(l.disp_qty ?? l.produced) || 0), 0);
    const v = items.reduce((a, l) => a + value_of(l), 0);
    return { q, d, v, p: Math.max(0, q - d) };
  };

  const save = async (submitAfter = false) => {
    if (savingRef.current) return;
    // Feb 2026 · Commercial Integrity Rules — hard-block save on empty
    // Customer / lines / line-delivery-dates. Highlight the offending
    // fields in red so the operator sees exactly what's missing.
    const errs = {};
    if (form.order_against !== "stock" && !form.customer_id) errs.customer_id = true;
    const items = form.items || [];
    if (!items.length) errs.items_empty = true;
    const hasValid = items.some(l => (Number(l.order_qty) || 0) > 0 && (Number(l.rate) || 0) > 0);
    if (items.length && !hasValid) errs.items_qty_rate = true;
    const missingLineDate = items.findIndex(l => !(l.delivery_date || "").trim());
    if (items.length && missingLineDate >= 0) errs[`line_delivery_${missingLineDate}`] = true;
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      if (errs.customer_id) return toast.error("Customer is required (mandatory)");
      if (errs.items_empty) return toast.error("Add at least one line item");
      if (errs.items_qty_rate) return toast.error("At least one line needs Qty > 0 AND Rate > 0");
      if (missingLineDate >= 0) return toast.error(`Line ${missingLineDate + 1} · Delivery Date is required`);
    }
    // Auto-header delivery = MAX of all line delivery dates.
    const lineDates = items.map(l => (l.delivery_date || "").trim()).filter(Boolean);
    if (lineDates.length) {
      const latest = lineDates.sort().slice(-1)[0];
      if (latest !== form.delivery_date) form.delivery_date = latest;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const itemsN = form.items.map((l) => {
        const orderQ = Number(l.order_qty) || 0;
        const pct = Number(l.extra_pct) || 0;
        // Mar 1, 2026 · Recompute extra_qty from the % on save so a stale
        // extra_qty (e.g. after amending Order Qty without re-touching
        // Extra %) can never leak into the payload.
        const extraQ = pct > 0 ? Math.round(orderQ * pct / 100) : (Number(l.extra_qty) || 0);
        return {
          ...l,
          order_qty: orderQ,
          extra_qty: extraQ,
          extra_pct: pct,
          disp_qty: Number(l.disp_qty) || 0,
          rate: Number(l.rate) || 0,
          quantity: orderQ + extraQ,
          produced: Number(l.disp_qty) || 0,
        };
      });
      // Feb 27, 2026 · Server-side race-safe assignment. The backend
      // POST /sales-orders/ now auto-reserves the next SO No when
      // `order_no` is blank, with a duplicate-key retry loop. No more
      // pre-reserve GET (that call intermittently failed under load and
      // wasn't race-safe against simultaneous saves anyway).
      const orderNo = form.order_no || "";
      const payload = { ...form, order_no: orderNo, items: itemsN };
      if (editing && isAmending) {
        if (!amendReason.trim() || amendReason.trim().length < 3) {
          toast.error("Amendment reason is required (min 3 chars)");
          return;
        }
        // Feb 2026 · Post-authorisation freeze — only administrative
        // fields (delivery date, priority, instructions, Customer PO No)
        // and a Hold / Cancel / Open (release) status flip may pass
        // through amend. Backend rejects any other key; keeping the
        // payload narrow gives the user a clean success instead of a 400.
        const ALLOWED_AMEND = ["delivery_date", "priority", "special_instructions", "po_number", "status", "hold_reason", "cancel_reason"];
        const amendUpdates = {};
        for (const k of ALLOWED_AMEND) {
          if (form[k] !== undefined && form[k] !== null) amendUpdates[k] = form[k];
        }
        const HOLD_CANCEL = new Set(["Hold", "Cancelled", "Open"]);
        if (amendUpdates.status && !HOLD_CANCEL.has(amendUpdates.status)) {
          toast.error("Amend can only set status to Hold, Cancelled or Open (release).");
          return;
        }
        if (amendUpdates.status === "Hold" && !(amendUpdates.hold_reason || "").trim()) {
          toast.error("Hold requires a reason");
          return;
        }
        if (amendUpdates.status === "Cancelled" && !(amendUpdates.cancel_reason || "").trim()) {
          toast.error("Cancel requires a reason");
          return;
        }
        await api.post(`/sales-orders/${editing.id}/amend`, { reason: amendReason.trim(), updates: amendUpdates });
        toast.success(`Amendment recorded for ${editing.order_no}`);
      } else if (editing) {
        await api.put(`/sales-orders/${editing.id}`, payload);
        if (submitAfter) {
          try {
            await api.post(`/sales-orders/${editing.id}/submit-for-approval`);
            toast.success(`${editing.order_no} · Submitted for Authorisation`);
          } catch (se) {
            toast.error("Order was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
          }
        } else {
          toast.success("Order updated");
        }
      } else {
        if (!createKeyRef.current) createKeyRef.current = uuidv4();
        const { data } = await api.post("/sales-orders/", payload, { headers: { "Idempotency-Key": createKeyRef.current } });
        createKeyRef.current = null;
        setEditing(data);
        if (submitAfter) {
          try {
            await api.post(`/sales-orders/${data.id}/submit-for-approval`);
            toast.success(`Order ${data?.order_no || orderNo} · Submitted for Authorisation`);
          } catch (se) {
            toast.error("Order was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
          }
        } else {
          toast.success(`Order ${data?.order_no || orderNo} created`, {
            action: {
              label: "→ Create Packing Slip",
              onClick: () => goToTab("slips", data?.id),
            },
            duration: 8000,
          });
        }
      }
      setOpen(false);
      setIsAmending(false);
      setAmendReason("");
      load();
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (!editing && createKeyRef.current && (!e?.response || st >= 500 || (st === 409 && /Still Processing/i.test(String(detail))))) {
        toast.error("Save not confirmed — your entries are kept. Retry safely: the same request is reused, so no duplicate order is created.", {
          action: { label: "Retry safely", onClick: () => save(submitAfter) },
          duration: 12000,
        });
        load();
      } else {
        toast.error(detail || "Save failed");
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const [cancelTarget, setCancelTarget] = useState(null); // { id, order_no } — opens the cancel dialog

  const remove = async (id, orderNo) => {
    // Feb 2026 · Ops sign-off — posted SOs get the reasoned cancel flow;
    // truly unposted Drafts can still be hard-deleted if the row is orphaned.
    setCancelTarget({ id, order_no: orderNo });
  };

  // PHASE-2 · Controlled Cancel Order for an authorised SO. Available even
  // when downstream exists (commercial-stop action). Opens the reason dialog.
  const cancelOrder = (id, orderNo) => setCancelOrderTarget({ id, order_no: orderNo });

  const addLine = () => {
    const next = emptyLine(form.items.length);
    // Rule 1 (Feb 2026 · user brief): every new line inherits the SO's top-
    // level Delivery Date so the buyer sees a consistent promise unless
    // Sales explicitly picks a different date per line below.
    next.delivery_date = form.delivery_date || next.delivery_date || "";
    setForm({ ...form, items: [...form.items, next] });
  };
  // Rule 2 (Feb 2026): SO top-level Delivery Date is the LATEST of every
  // line's `delivery_date`. Empty line-dates fall back to the current SO
  // date so a blank line never drags the top-level date backwards.
  const recomputeTopDelivery = (items, currentTop) => {
    const dates = items.map(li => li.delivery_date || currentTop).filter(Boolean);
    if (!dates.length) return currentTop;
    return dates.reduce((a, b) => (a > b ? a : b));
  };
  const updateLine = (i, k, v) => {
    const arr = [...form.items];
    arr[i][k] = v;
    // Mar 1, 2026 · Extra Qty is now driven by a % of Order Qty (parity
    // with ESO). Editing Order Qty or Extra % recomputes extra_qty; a
    // direct edit to extra_qty still sticks (manual override wins on
    // legacy amendments).
    if (k === "order_qty") {
      const q = Number(v) || 0;
      const pct = Number(arr[i].extra_pct) || 0;
      arr[i].extra_qty = Math.round(q * pct / 100);
    } else if (k === "extra_pct") {
      const q = Number(arr[i].order_qty) || 0;
      const pct = Number(v) || 0;
      arr[i].extra_qty = Math.round(q * pct / 100);
    }
    if (k === "item_id") {
      const it = items.find(x => x.id === v);
      if (it) {
        arr[i].item_code = it.code;
        if (!arr[i].description) arr[i].description = it.description;
        if (!arr[i].colour) arr[i].colour = it.color;
        if (!arr[i].size) arr[i].size = `${it.width_mm}mm`;
      }
    }
    if (k === "article_id") {
      const art = articles.find(a => a.id === v);
      if (art) {
        arr[i].article_no = art.product_code;
        arr[i].description = art.description || arr[i].description;
        arr[i].colour = art.colour || arr[i].colour;
        arr[i].size = art.size || arr[i].size;
        // Auto-fill Style No from the Article master (Feb 2026) so Sales
        // stops entering it twice. Only overwrites when the SO line's
        // style_no is still blank — planner can override if a customer
        // needs a different reference for THIS particular SO line.
        if (!arr[i].style_no && art.style_no) {
          arr[i].style_no = art.style_no;
        }
        // Feb 2026 · Auto-inherit HS Code from the Article Master (which
        // itself inherits from the Article Type). Ensures domestic invoices
        // carry the tariff heading straight from the first save.
        if (!arr[i].hs_code && art.hs_code) {
          arr[i].hs_code = art.hs_code;
        }
      }
    }
    // Rule 2 propagation — a line's delivery_date change may lift the SO's
    // top-level Delivery Date. Only ever moves it later, never earlier, so
    // shorter-lead lines can't shorten the SO's headline promise.
    let nextTopDelivery = form.delivery_date;
    if (k === "delivery_date") {
      nextTopDelivery = recomputeTopDelivery(arr, form.delivery_date);
    }
    setForm({ ...form, items: arr, delivery_date: nextTopDelivery });
  };
  const removeLine = (i) => {
    const remaining = form.items.filter((_, x) => x !== i).map((li, idx) => ({
      ...li,
      item_no: String(idx + 1).padStart(3, "0"),
    }));
    setForm({ ...form, items: remaining });
  };
  const customerName = (id) => customers.find(c => c.id === id)?.name || "—";

  const printSo = (o) => {
    // Feb 2026 · Route SO print via the shared DocumentPrintTemplate so
    // the browser sees the same A4 HTML layout used for PI + PO.
    printDocumentHtml({ kind: "so", id: o.id });
  };
  // Legacy client-side rendering — retained as a fallback while the
  // shared PI-template roll-out finishes for the SO report tab.
  const printSoLegacy = (o) => {
    const cust = customers.find(c => c.id === o.customer_id) || {};
    const addrs = Array.isArray(cust?.addresses) ? cust.addresses : [];
    // Feb 2026 · Prefer the stored snapshot (immortalised at SO creation
    // time). Fall back to resolving from the address_id, then to the
    // customer's primary addresses. This ensures printouts remain
    // historically accurate even if Customer Master is later edited.
    const _resolve = (snap, id, prefer) => {
      const s = snap && Object.keys(snap || {}).length ? snap : null;
      if (s) return s;
      const byId = addrs.find(a => a.id === id);
      if (byId) return byId;
      return addrs.find(a => a[prefer]) || addrs[0] || {};
    };
    const bill = _resolve(o.bill_to_snapshot, o.bill_to_address_id, "is_primary_bill");
    const ship = _resolve(o.ship_to_snapshot, o.ship_to_address_id, "is_primary_ship");
    // Feb 2026 · Structured address blocks — each field maps to its own
    // slot on the print template so long addresses no longer collapse
    // into a cramped meta cell. The tag surfaces the address label the
    // user picked from the Customer Master ("Head Office", "Warehouse A").
    const toBlock = (title, a, custName) => {
      if (!a || Object.keys(a).length === 0) return { title, name: custName || "—" };
      const rawLbl = a.label === "Other" ? (a.custom_label || "Other") : (a.label || "");
      const locale = [a.city, a.state, a.pincode].filter(Boolean).join(", ");
      const metaRows = [];
      if (a.gstin) metaRows.push(["GSTIN", a.gstin]);
      if (a.contact_person) metaRows.push(["Attn", a.contact_person]);
      if (a.phone) metaRows.push(["Phone", a.phone]);
      if (a.email) metaRows.push(["Email", a.email]);
      return {
        title,
        tag: rawLbl && rawLbl.toLowerCase() !== title.toLowerCase() ? rawLbl : "",
        name: custName || "—",
        street: a.address || "",
        locale,
        country: a.country || "",
        metaRows,
      };
    };
    const t = orderTotals(o.items || []);
    const itemRows = (o.items || []).map((li, i) => {
      const m = (items || []).find(x => (x.description || "") === li.article_no || (x.code || "") === li.article_no || x.id === li.item_id);
      const hs = (m && m.hs_code) || li.hs_code || "";
      return {
        item_no: li.item_no || String(i + 1).padStart(3, "0"),
        article_no: li.article_no || "",
        hs_code: hs,
        order_qty: Number(li.order_qty) || 0,
        extra_qty: Number(li.extra_qty) || 0,
      total_qty: total_qty(li),
      rate: Number(li.rate) || 0,
      value: value_of(li),
      };
    });
    printDocument({
      kind: "Sales Order",
      no: o.order_no,
      // Feb 2026 · Meta trimmed to the six essential order-level fields
      // so the top block is scannable at a glance. Customer identity and
      // full addresses live in the dedicated addressBlocks below.
      meta: [
        { label: "Order Type", value: o.order_against === "stock" ? "Against Stock" : "Against Customer PO" },
        { label: "Order Date", value: o.order_date },
        { label: "Delivery Date", value: o.delivery_date },
        { label: "Customer PO No", value: o.po_number || "—" },
        { label: "Priority", value: o.priority },
        { label: "Status", value: o.status + (o.authorized ? " · AUTHORIZED" : "") },
      ],
      addressBlocks: o.order_against === "stock" ? [] : [
        toBlock("Bill To", bill, cust.name),
        toBlock("Ship To", ship, cust.name),
      ],
      sections: [
        {
          heading: `Order Line Items · ${itemRows.length}`,
          columns: [
            { key: "item_no", label: "#", mono: true },
            { key: "article_no", label: "Product Code", mono: true },
            { key: "hs_code", label: "HS Code", mono: true },
            { key: "order_qty", label: "Order Qty", align: "right", fmt: v => fmtQtyInt(v) },
            { key: "extra_qty", label: "Buffer Qty", align: "right", fmt: v => Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 }) },
            { key: "total_qty", label: "Total", align: "right", fmt: v => fmtQtyInt(v) },
            { key: "rate", label: "Rate", align: "right", fmt: v => fmtInr(v) },
            { key: "value", label: "Value", align: "right", fmt: v => fmtInr(v) },
          ],
          rows: itemRows,
          footerRow: {
            item_no: "TOTAL",
            total_qty: fmtQtyInt(t.q),
            value: fmtInr(t.v),
          },
        },
      ],
      signatures: ["Prepared By", "Checked By", "Authorized"],
      footer: o.special_instructions ? `Special Instructions: ${o.special_instructions}` : "",
    });
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-0.5">Order Management</div>
          <h1 className="font-display text-2xl font-black tracking-tight">Sales Orders</h1>
        </div>
        <div className="flex items-center gap-2">
        {subTab === "orders" && (<>
        <PermButton
          module="sales_orders"
          action="edit"
          data-testid="new-order-btn"
          onClick={openNew}
          className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2"
        >
          <PlusIcon size={14} weight="bold" />New Order
        </PermButton>
        <button
          data-testid="export-orders-csv"
          onClick={() => {
            const rows = orders.map(o => {
              const t = orderTotals(o.items || []);
              return {
                order_no: o.order_no, sales_date: o.order_date,
                customer: o.order_against === "stock" ? "Stock" : customerName(o.customer_id),
                po: o.po_number, delivery: o.delivery_date,
                priority: o.priority, status: o.status,
                total_qty: fmtQtyInt(t.q), value: fmtInr(t.v, {symbol: false}),
              };
            });
            exportToCSV("sales-orders.csv", rows, [
              { key: "order_no", label: "Sales Order No" },
              { key: "sales_date", label: "Sales Date" },
              { key: "customer", label: "Customer" },
              { key: "po", label: "PO" },
              { key: "delivery", label: "Delivery" },
              { key: "priority", label: "Priority" },
              { key: "status", label: "Status" },
              { key: "total_qty", label: "Total Qty" },
              { key: "value", label: "Value" },
            ]);
          }}
          className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"
        >
          <DownloadSimpleIcon size={14} weight="bold" />Export CSV
        </button>
        </>)}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {[
          { key: "orders", label: "Orders" },
          { key: "slips", label: "Packing Slips" },
          { key: "packing", label: "Packing Lists" },
          { key: "challans", label: "Delivery Challans" },
          { key: "report", label: "Order Status" },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`so-subtab-${t.key}`}
            onClick={() => setSubTab(t.key)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${subTab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "packing" && <PackingListsTab onNextStep={goToTab} />}
      {subTab === "slips" && <PackingSlipsTab onNextStep={goToTab} mode="domestic" />}
      {subTab === "challans" && <DeliveryChallansTab onNextStep={goToTab} />}
      {subTab === "report" && <SoStatusReport />}
      {subTab === "orders" && (<>

      {/* SO Register UI/UX Pilot V1 · KPI analytical strip. Calculations are
          preserved EXACTLY (per-line roll-up, pending-eligibility parity with
          backend lib_pending / is_pending_eligible_status). Presentation only. */}
      {(() => {
        const PENDING_INELIGIBLE = new Set([
          "hold", "on hold", "short closed", "shortclosed", "short close",
          "cancelled", "canceled", "completed", "closed", "rejected",
        ]);
        const statusKey = (s) => String(s || "").trim().toLowerCase().replace(/[-_]/g, " ").replace(/\s+/g, " ");
        const totals = orders.reduce((a, o) => {
          // R2-01L A4-02 · G5/D4 · PSOR-01 eligibility (authorised + lifecycle); submitted approvals shown apart.
          const pendingEligible = !PENDING_INELIGIBLE.has(statusKey(o.status)) && isActionablePending("so", o);
          const awaitingApproval = isSubmittedPendingApproval("so", o);
          for (const li of (o.items || [])) {
            const oq = Number(li.order_qty) || 0;
            const dq = Number(li.disp_qty) || 0;
            const cx = Number(li.cancelled_qty) || 0;
            const rt = Number(li.rate) || 0;
            const shortClosed = !!li.short_closed;
            const pending = (pendingEligible && !shortClosed) ? Math.max(0, oq - cx - dq) : 0;
            if (awaitingApproval && !shortClosed) a.approval += Math.max(0, oq - cx - dq);
            a.total    += oq;
            a.disp     += dq;
            a.pending  += pending;
            a.totalVal += Math.max(0, oq - cx) * rt;
            a.dispVal  += dq * rt;
            a.pendVal  += pending * rt;
          }
          return a;
        }, { total: 0, disp: 0, pending: 0, totalVal: 0, dispVal: 0, pendVal: 0, approval: 0 });
        const pend = totals.pending;
        const fmtI = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
        const Tile = ({ label, value, cls, tid, hint }) => (
          <div className="flex flex-col gap-0.5 px-3.5 py-1.5" title={hint}>
            <span className="text-[9px] uppercase tracking-[0.18em] text-[var(--fg-2)]">{label}</span>
            <span className={`font-mono tabular-nums text-[15px] font-semibold leading-none ${cls}`} data-testid={tid}>{value}</span>
          </div>
        );
        return (
          <div data-testid="so-summary-bar" className="flex flex-wrap items-stretch rounded-md border border-[var(--border)] bg-[var(--bg-1)] overflow-hidden divide-x divide-[var(--border)]">
            <div className="flex items-center px-3.5">
              <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--accent)]">Fulfilment</span>
            </div>
            <Tile label="Orders" value={orders.length} cls="text-white" tid="so-summary-orders" />
            <Tile label="Order Qty · m" value={fmtQtyInt(totals.total)} cls="text-emerald-300" tid="so-summary-total" />
            <Tile label="Dispatched" value={fmtQtyInt(totals.disp)} cls="text-sky-300" tid="so-summary-disp" />
            <Tile label="Pending" value={fmtQtyInt(pend)} cls={pend > 0 ? "text-amber-300" : "text-emerald-300"} tid="so-summary-pending" hint="Actionable pending · authorised orders only (same rule as the Pending Order Statement)" />
            <Tile label="Pending Approval" value={fmtQtyInt(totals.approval)} cls="text-zinc-300" tid="so-summary-pending-approval" hint="Submitted orders awaiting authorisation · not included in actionable Pending" />
            <div className="flex items-center px-3.5 bg-[var(--bg-2)]/30">
              <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fg-2)]">Commercial</span>
            </div>
            <Tile label="Total Value" value={fmtI(totals.totalVal)} cls="text-white" tid="so-summary-total-value" />
            <Tile label="Dispatched Value" value={fmtI(totals.dispVal)} cls="text-emerald-300" tid="so-summary-disp-value" />
            <Tile label="Pending Value" value={fmtI(totals.pendVal)} cls={totals.pendVal > 0 ? "text-amber-300" : "text-emerald-300"} tid="so-summary-pend-value" />
          </div>
        );
      })()}

      {/* SO Register UI/UX Pilot V1 · unified compact filter toolbar —
          search · expand/collapse · density · columns (all existing behavior). */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-md">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="orders-search"
            value={orderSearch}
            onChange={e => setOrderSearch(e.target.value)}
            placeholder="Search order no, customer, PO, product code…"
            className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
        {orderSearch && (
          <button data-testid="orders-search-clear" onClick={() => setOrderSearch("")} className="h-9 px-3 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
        )}
        <div className="flex-1" />
        <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" role="group" aria-label="Row density">
          {[["comfortable", "Comfy"], ["compact", "Compact"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`so-density-${k}`}
              onClick={() => setDensity(k)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 ${soDensity === k ? "bg-[var(--accent)]/15 text-[var(--accent)]" : "text-zinc-400 hover:text-zinc-200"}`}
            >{l}</button>
          ))}
        </div>
        <div className="relative">
          <button
            data-testid="so-orders-cols-btn"
            onClick={() => setSoShowColsMenu(v => !v)}
            className="h-[30px] text-[11px] font-mono uppercase tracking-widest px-3 border border-[var(--border)] hover:border-[var(--accent)] rounded-sm text-zinc-300 flex items-center gap-2"
            title="Show / hide columns"
          >
            <span className="font-mono">{soVisibleCols.size}/{COL_ORDER.length}</span> Columns
          </button>
          {soShowColsMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSoShowColsMenu(false)} />
              <div className="absolute right-0 top-9 z-40 w-64 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl p-2 max-h-96 overflow-y-auto" data-testid="so-orders-cols-menu">
                <div className="flex items-center justify-between px-1 pb-2 border-b border-[var(--border)] mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Show columns</div>
                  <button onClick={soResetCols} className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline" data-testid="so-orders-cols-reset">Reset</button>
                </div>
                {COL_ORDER.map(key => {
                  const c = COLS[key];
                  if (!c) return null;
                  const pinned = SO_PINNED_COLS.has(key);
                  const baselineSticky = !!c.sticky;
                  const userPinned = soPinnedKeys.has(key);
                  const isPinned = baselineSticky || userPinned;
                  const canUserPin = !baselineSticky;
                  return (
                    <div key={key} className={`flex items-center gap-2 px-2 py-1 rounded-sm text-xs hover:bg-[var(--bg-2)] ${pinned ? "opacity-60" : ""}`}>
                      <input type="checkbox"
                        data-testid={`so-orders-col-${key}`}
                        checked={soVisibleCols.has(key) || pinned}
                        disabled={pinned}
                        onChange={() => toggleSoCol(key)}
                        className="w-3.5 h-3.5 accent-[var(--accent)]" />
                      <span className="text-zinc-200 flex-1">{c.label}</span>
                      {pinned && <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">visible</span>}
                      <button
                        type="button"
                        data-testid={`so-orders-pin-${key}`}
                        onClick={() => canUserPin && toggleSoPin(key)}
                        disabled={!canUserPin}
                        title={baselineSticky ? "Product Code is always pinned" : (userPinned ? "Unpin column" : "Pin column")}
                        className={`text-[10px] px-1.5 py-0.5 rounded-sm border font-mono ${isPinned ? "border-[var(--accent)]/60 bg-[var(--accent)]/15 text-[var(--accent)]" : "border-[var(--border)] text-zinc-500 hover:text-zinc-200"} ${!canUserPin ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                      >📌</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="tactical-card overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-400 border-b border-[var(--border)] sticky top-0 z-30 bg-[var(--bg-1)] shadow-[0_1px_0_var(--border)]">
              {soVisCols.map(key => {
                const col = COLS[key];
                if (!col) return null;
                return (
                  <th
                    key={key}
                    data-testid={`col-${key}`}
                    style={(col.sticky || soPinnedKeys.has(key)) ? { left: `${stickyLefts[key] || 0}px` } : undefined}
                    className={`${cellPad} font-semibold select-none ${col.align === "right" ? "text-right" : "text-left"} ${col.width || ""} ${(col.sticky || soPinnedKeys.has(key)) ? "sticky z-30 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]" : ""}`}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(() => {
              const q = orderSearch.trim().toLowerCase();
              const list = !q ? orders : orders.filter(o =>
                (o.order_no || "").toLowerCase().includes(q) ||
                (o.po_number || "").toLowerCase().includes(q) ||
                ((o.order_against === "stock" ? "Stock" : customerName(o.customer_id)) || "").toLowerCase().includes(q) ||
                (o.items || []).some(li => (li.article_no || "").toLowerCase().includes(q))
              );
              const today = new Date().toISOString().slice(0, 10);
              // SO Register V1 · CORRECTION 02 — TRUE LINE-WISE REGISTER.
              // One SO line item = one visible row. NO parent/summary row, NO
              // expand/collapse, NO aggregate row. Order-level identity
              // (SO No · Date · Customer · PO) repeats on every line via the
              // COLS renders (no blanking / no ditto). KPI strip stays SO-based.
              return (<>
                {list.flatMap(o => {
                  const t = orderTotals(o.items || []);
                  const lines = (o.items || []);
                  const linesToRender = lines.length ? lines : [null];
                  const isClosed = (o.status || "").toLowerCase() === "closed";
                  const isSoCancelled = (o.status || "").toLowerCase() === "cancelled";
                  const canEditRow = hasPerm("sales_orders:edit");
                  const canCancelRow = hasPerm("sales_orders:delete");
                  const canPrintRow = hasPerm("sales_orders:print");
                  return linesToRender.map((line, lineIdx) => {
                    const isFirst = lineIdx === 0;
                    const isLast = lineIdx === linesToRender.length - 1;
                    const lineDelivery = line?.delivery_date || o.delivery_date;
                    const isOverdue = !isClosed && lineDelivery && lineDelivery < today;
                    const ctx = { o, t, line, lineIdx, isFirst, isLast, lineDelivery, customerName, authorize, submitForApproval, canAuthorize, canEdit, openEdit, remove, cancelOrder, setHistoryOrder, setPreview, printSo, setArticleFilter, articles, items, openBulkForLine, canBulk: hasPerm("dispatch:edit"), canEditRow, canCancelRow, canPrintRow, downstreamLock: downstreamLocks[o.id] || "", woSummary, setModifyTarget, setModifyOpen, setAdminAmend, setHoldAction };
                    return (
                      <tr
                        key={`${o.id}-${lineIdx}`}
                        className={`${isLast ? "border-b border-[var(--border)]" : "border-b border-[var(--border)]/40"} align-top ${isOverdue ? "bg-amber-500/[0.05] hover:bg-amber-500/[0.08]" : "hover:bg-[var(--bg-2)]/40"} ${isSoCancelled ? "[&>td]:text-zinc-500 opacity-75" : ""} ${isFirst && flashingOrderIds.has(o.id) ? "row-flash-emerald" : ""}`}
                        data-testid={`order-line-${o.order_no}-${lineIdx}`}
                      >
                        {soVisCols.map(key => {
                          const col = COLS[key];
                          if (!col) return null;
                          const widthCls = col.width || "";
                          const isPinned = col.sticky || soPinnedKeys.has(key);
                          const stickyCls = isPinned ? "sticky z-20 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]" : "";
                          const style = isPinned ? { left: `${stickyLefts[key] || 0}px` } : undefined;
                          return <td key={key} style={style} className={`${cellPad} ${col.align === "right" ? "text-right" : ""} ${widthCls} ${stickyCls}`}>{col.render(ctx)}</td>;
                        })}
                      </tr>
                    );
                  });
                })}
                {orders.length === 0 && <tr><td colSpan={soVisCols.length} className="text-center py-16 text-zinc-500">No orders yet</td></tr>}
                {orders.length > 0 && list.length === 0 && <tr><td colSpan={soVisCols.length} className="text-center py-10 text-zinc-500">No orders match "{orderSearch}"</td></tr>}
              </>);
            })()}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[var(--bg-1)] border-[var(--border)] max-w-[95vw] max-h-[92vh] overflow-y-auto"
          data-testid="order-dialog"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {editing ? (isAmending ? "Amend Order" : "Edit Order") : "New Sales Order — Manual Entry"}
              {form.order_against === "stock" && (
                <span data-testid="so-dialog-stock-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-emerald-500/15 text-emerald-400">
                  <StackIcon size={10} weight="bold" />AGAINST STOCK
                </span>
              )}
              {editing?.authorized && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-emerald-500/10 text-emerald-400">
                  <LockIcon size={10} weight="bold" />AUTHORISED
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Mar 1, 2026 · Per-doc Approval Workflow (Admin/Manager only) */}
          {editing?.id && (
            <ApprovalControls
              docType="so"
              docId={editing.id}
              docNo={editing.order_no}
              approvalStatus={editing.approval_status || (editing.authorized ? "Authorized" : "Draft")}
              rejectionReason={editing.rejection_reason}
              approvalHistory={editing.approval_history}
              onChange={async () => { await load(); }}
              testIdPrefix="so-approval"
            />
          )}

          {/* Feb 2026 · Order-Against segmented tab. Switches between the
              traditional Customer-PO flow and Against-Stock (build for stock,
              no customer required). Stock hides Customer + PO fields below. */}
          <div className="flex items-center gap-3 mb-3" data-testid="so-order-against-tabs">
            <div className="overline">Order Type</div>
            <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
              {[
                { k: "customer_po", label: "Against Customer PO" },
                { k: "stock",       label: "Against Stock" },
              ].map(t => (
                <button
                  key={t.k}
                  type="button"
                  data-testid={`so-order-against-${t.k}`}
                  onClick={() => setForm(f => ({
                    ...f,
                    order_against: t.k,
                    // Clear customer + PO when switching to Stock so the
                    // payload is clean; keep values when switching back so a
                    // mis-click doesn't wipe user input.
                    ...(t.k === "stock" ? { customer_id: "", po_number: "", fulfilment_mode: "production_sales" } : {}),
                  }))}
                  className={`px-3 h-8 text-[11px] font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${form.order_against === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                >{t.label}</button>
              ))}
            </div>
            {form.order_against === "stock" && (
              <span className="text-[10px] text-emerald-400 font-mono uppercase tracking-widest" title="Customer & PO fields are hidden — this SO builds for stock.">Stock Only — No Sales · no customer required</span>
            )}
            {form.order_against !== "stock" && (
              <FulfilmentChoice prefix="so" value={form.fulfilment_mode} disabled={isAmending}
                onChange={(k) => setForm(f => ({ ...f, fulfilment_mode: k }))} />
            )}
          </div>

          {isAmending && (
            <div className="p-3 rounded-sm border border-amber-500/30 bg-amber-500/5 space-y-2" data-testid="amend-banner">
              <div className="flex items-start gap-2">
                <LockIcon size={16} weight="bold" className="text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-400">Authorised Order — Qty · Rate · Items · Customer are FROZEN</div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    Only <b>Customer PO No</b>, <b>Delivery Date</b>, <b>Priority</b>, <b>Instructions</b> and a <b>Hold / Cancel / Open</b> status flip (with reason) can be amended.
                    To change quantity, rate or line items, put the SO on <b>Hold</b> or <b>Cancel</b> it and raise a fresh SO.
                  </div>
                </div>
              </div>
              <input
                data-testid="amend-reason"
                value={amendReason}
                onChange={e => setAmendReason(e.target.value)}
                placeholder="Amendment reason (required, min 3 chars)"
                className="w-full h-9 px-3 bg-[var(--bg-0)] border border-amber-500/30 rounded-sm text-sm focus:outline-none focus:border-amber-400"
              />
              {/* Amend-mode status controls · only Hold / Cancelled / Open
                  (release) allowed. Reason is required for Hold + Cancel. */}
              <div className="grid grid-cols-3 gap-2 pt-1">
                <div>
                  <div className="overline mb-1 text-[10px]">Status action</div>
                  <Select
                    value={form.status && ["Hold", "Cancelled", "Open"].includes(form.status) ? form.status : "Open"}
                    onValueChange={v => setForm({ ...form, status: v })}
                  >
                    <SelectTrigger data-testid="amend-status" className={selCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                      <SelectItem value="Open">Open / Release</SelectItem>
                      <SelectItem value="Hold">Hold</SelectItem>
                      <SelectItem value="Cancelled">Cancel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.status === "Hold" && (
                  <div className="col-span-2">
                    <div className="overline mb-1 text-[10px] text-amber-400">Hold Reason *</div>
                    <input data-testid="amend-hold-reason" value={form.hold_reason || ""} onChange={e => setForm({ ...form, hold_reason: e.target.value })} className="w-full h-8 px-2 bg-[var(--bg-0)] border border-amber-500/30 rounded-sm text-sm focus:outline-none focus:border-amber-400" placeholder="Why is this order on hold?" />
                  </div>
                )}
                {form.status === "Cancelled" && (
                  <div className="col-span-2">
                    <div className="overline mb-1 text-[10px] text-red-400">Cancel Reason *</div>
                    <input data-testid="amend-cancel-reason" value={form.cancel_reason || ""} onChange={e => setForm({ ...form, cancel_reason: e.target.value })} className="w-full h-8 px-2 bg-[var(--bg-0)] border border-red-500/30 rounded-sm text-sm focus:outline-none focus:border-red-400" placeholder="Why is this order being cancelled?" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Header */}
          <div className="grid grid-cols-6 gap-3">
            <F label={editing ? "Sales Order No" : "Sales Order No · reserved on save"}>
              {editing ? (
                <input data-testid="input-order-no" value={form.order_no} readOnly className={inp + " opacity-70 cursor-not-allowed"} title="SO number is immutable once created" />
              ) : (
                <input data-testid="input-order-no" value="" readOnly className={inp + " italic text-zinc-500 cursor-not-allowed"} placeholder="Auto-assigned on Save · race-safe" title="A fresh SO No is reserved from the server the moment you click Save — so two operators drafting at the same time can never collide" />
              )}
            </F>
            <F label="Sales Date"><input type="date" data-testid="input-sales-date" value={form.order_date} onChange={e => setForm({...form, order_date: e.target.value})} className={inp} /></F>
            {form.order_against === "stock" && (
              <F label="Customer">
                <input data-testid="input-customer-stock" value="Stock" readOnly className={inp + " opacity-80 cursor-not-allowed text-emerald-400 font-semibold"} title="Against Stock · no customer" />
              </F>
            )}
            {form.order_against !== "stock" && (
              <F label="Customer">
                {isAmending ? (
                  <div data-testid="input-customer-locked" className={inp + " opacity-70 cursor-not-allowed flex items-center gap-2"} title="Customer is frozen after authorisation">
                    <LockIcon size={11} weight="bold" className="text-amber-400" />
                    <span className="truncate">{customerName(form.customer_id)}</span>
                  </div>
                ) : (
                  <CustomerCombobox
                    className={fieldErrors.customer_id ? "ring-1 ring-red-500/60 rounded" : ""}
                    customers={customers}
                    value={form.customer_id}
                    onChange={(v) => {
                      // Feb 2026 · When Customer changes, reset the per-order
                      // Bill To / Ship To so the primary defaults kick in for
                      // the newly-selected customer's address list.
                      setForm({ ...form, customer_id: v, bill_to_address_id: "", ship_to_address_id: "", bill_to_snapshot: {}, ship_to_snapshot: {} });
                      setFieldErrors(e => ({ ...e, customer_id: false }));
                    }}
                    onCreated={(cust) => { setCustomers(prev => [...prev, cust]); setForm(f => ({ ...f, customer_id: cust.id, bill_to_address_id: "", ship_to_address_id: "", bill_to_snapshot: {}, ship_to_snapshot: {} })); setFieldErrors(e => ({ ...e, customer_id: false })); }}
                  />
                )}
              </F>
            )}
            {form.order_against !== "stock" && (
              <F label="Customer PO NO">
                <input
                  data-testid="input-po-number"
                  value={form.po_number}
                  onChange={e => setForm({...form, po_number: e.target.value})}
                  className={inp}
                  title={isAmending ? "Editable during Amendment — buyers often reissue a corrected PO reference" : ""}
                />
              </F>
            )}
            {/* Feb 2026 · Per-order Bill To / Ship To pickers · read from the
                selected Customer Master's `addresses[]`. Defaults to primary
                bill / ship when blank; empty state (no customer / no
                addresses) hides the cells. */}
            {form.order_against !== "stock" && form.customer_id && (() => {
              const cust = customers.find(c => c.id === form.customer_id);
              const addrs = Array.isArray(cust?.addresses) ? cust.addresses : [];
              if (addrs.length === 0) return null;
              const primaryBill = addrs.find(a => a.is_primary_bill) || addrs[0];
              const primaryShip = addrs.find(a => a.is_primary_ship) || addrs[0];
              const optLabel = (a) => {
                const lbl = a.label === "Other" ? (a.custom_label || "Other") : a.label;
                const loc = [a.city, a.state].filter(Boolean).join(", ");
                return `${lbl}${loc ? " · " + loc : ""}`;
              };
              // Extract only the printable address fields for the SO snapshot.
              const _toSnap = (a) => a ? ({
                label: a.label || "", custom_label: a.custom_label || "",
                address: a.address || "", city: a.city || "",
                state: a.state || "", pincode: a.pincode || "",
                country: a.country || "", gstin: a.gstin || "",
                contact_person: a.contact_person || "",
                phone: a.phone || "", email: a.email || "",
              }) : {};
              const applyPick = (which, addrId) => {
                const picked = addrs.find(a => a.id === addrId) || null;
                setForm(f => ({
                  ...f,
                  [`${which}_to_address_id`]: addrId,
                  [`${which}_to_snapshot`]: _toSnap(picked),
                }));
              };
              return (
                <>
                  <F label="Bill To">
                    <Select
                      value={form.bill_to_address_id || primaryBill?.id || ""}
                      onValueChange={v => applyPick("bill", v)}
                    >
                      <SelectTrigger data-testid="so-bill-to" className={selCls}><SelectValue placeholder="Pick Bill To…" /></SelectTrigger>
                      <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                        {addrs.map(a => <SelectItem key={a.id} value={a.id} data-testid={`so-bill-to-opt-${a.id.slice(-6)}`}>{optLabel(a)}{a.is_primary_bill ? " ★" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Ship To">
                    <Select
                      value={form.ship_to_address_id || primaryShip?.id || ""}
                      onValueChange={v => applyPick("ship", v)}
                    >
                      <SelectTrigger data-testid="so-ship-to" className={selCls}><SelectValue placeholder="Pick Ship To…" /></SelectTrigger>
                      <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                        {addrs.map(a => <SelectItem key={a.id} value={a.id} data-testid={`so-ship-to-opt-${a.id.slice(-6)}`}>{optLabel(a)}{a.is_primary_ship ? " ★" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                  {/* Feb 2026 · Full-address preview + one-time override. The
                      dropdown above merely picks WHICH saved address; the
                      block below shows every field so users can spot the
                      wrong pick, and hand-edit one field (e.g. warehouse
                      pincode) for this order without touching the master. */}
                  <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SoAddressBlock
                      title="Bill To — Full Address"
                      testId="so-bill-to-snap"
                      snapshot={form.bill_to_snapshot}
                      readOnly={isAmending}
                      fallback={() => _toSnap(addrs.find(a => a.id === (form.bill_to_address_id || primaryBill?.id)))}
                      onChange={(patch) => setForm(f => ({ ...f, bill_to_snapshot: { ...(f.bill_to_snapshot || {}), ...patch } }))}
                    />
                    <SoAddressBlock
                      title="Ship To — Full Address"
                      testId="so-ship-to-snap"
                      snapshot={form.ship_to_snapshot}
                      readOnly={isAmending}
                      fallback={() => _toSnap(addrs.find(a => a.id === (form.ship_to_address_id || primaryShip?.id)))}
                      onChange={(patch) => setForm(f => ({ ...f, ship_to_snapshot: { ...(f.ship_to_snapshot || {}), ...patch } }))}
                      onCopyFromBill={() => setForm(f => ({ ...f, ship_to_snapshot: { ...(f.bill_to_snapshot || {}) } }))}
                    />
                  </div>
                </>
              );
            })()}
            <F label="Delivery Date">
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  data-testid="so-delivery-date"
                  value={form.delivery_date}
                  title="Auto-updates to the latest line-item delivery date. Editing this here also fills every blank line's date so all lines start in sync."
                  onChange={e => {
                    // Rule 1 propagation — when Sales edits the SO's headline
                    // Delivery Date, every line whose date is blank OR was
                    // still tied to the OLD headline date is snapped to the
                    // new value so the buyer promise stays consistent. Lines
                    // that already carry an explicit different date are left
                    // untouched — Sales chose them for a reason.
                    const newTop = e.target.value;
                    const oldTop = form.delivery_date;
                    const nextItems = form.items.map(li => (
                      (!li.delivery_date || li.delivery_date === oldTop)
                        ? { ...li, delivery_date: newTop }
                        : li
                    ));
                    setForm({ ...form, delivery_date: newTop, items: nextItems });
                  }}
                  className={inp + " flex-1"}
                />
                <button
                  type="button"
                  data-testid="so-bulk-line-date-btn"
                  disabled={!form.delivery_date || !(form.items || []).length}
                  onClick={() => {
                    // Feb 2026 · Bulk Line Date — force EVERY line's
                    // delivery_date to the header value (overwrites even
                    // lines that already carry an explicit different date).
                    // Handy when the buyer converges on a single deadline
                    // late in the negotiation.
                    const target = form.delivery_date;
                    const items = form.items || [];
                    if (!target) return toast.error("Set a Delivery Date first");
                    if (!items.length) return toast.error("Add at least one line item");
                    const changed = items.filter(li => li.delivery_date !== target).length;
                    setForm({ ...form, items: items.map(li => ({ ...li, delivery_date: target })) });
                    toast.success(`Applied ${target} to ${items.length} line${items.length === 1 ? "" : "s"}${changed ? "" : " (already in sync)"}`);
                  }}
                  title="Force every line's delivery date to the header value"
                  className="h-9 px-2 text-[10px] font-semibold uppercase tracking-widest border border-[var(--border)] bg-[var(--bg-2)]/60 text-zinc-300 hover:text-white hover:border-[var(--accent)]/60 hover:bg-[var(--accent)]/10 rounded-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Apply&nbsp;All
                </button>
              </div>
            </F>
            <F label="Priority">
              <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {["Low","Normal","High","Critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            {isAmending ? (
              <F label="Status">
                <div data-testid="input-status-locked" className={inp + " opacity-70 cursor-not-allowed flex items-center gap-2"} title="Status is managed in the Amendment banner above (Hold / Cancel / Open only)">
                  <LockIcon size={11} weight="bold" className="text-amber-400" />
                  <span>{form.status}</span>
                </div>
              </F>
            ) : (
              <F label="Status">
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {["Open","Planned","Running","Completed","Closed","Cancelled"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            )}
            <F label="Instructions" className="col-span-5"><input data-nouppercase value={form.special_instructions} onChange={e => setForm({...form, special_instructions: e.target.value})} className={inp} placeholder="Free text — any special instructions for the shop floor" /></F>
          </div>

          {/* Line items table */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="overline flex items-center gap-2">
                Line Items
                {isAmending && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-400"><LockIcon size={9} weight="bold" />Frozen</span>
                )}
              </div>
              {!isAmending && (
                <button onClick={addLine} data-testid="add-line" className="text-xs text-[var(--accent)] font-semibold flex items-center gap-1">
                  <PlusIcon size={12} weight="bold" /> Add line
                </button>
              )}
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[var(--bg-2)] uppercase tracking-widest text-[10px] text-zinc-500">
                    <th className="px-2 py-2 text-left font-medium w-16">Item No</th>
                    <th className="px-2 py-2 text-left font-medium w-28">Style No</th>
                    <th className="px-2 py-2 text-left font-medium">Product Code</th>
                    <th className="px-2 py-2 text-left font-medium w-28">HS Code</th>
                    <th className="px-2 py-2 text-right font-medium bg-pink-500/10">Order Qty</th>
                    <th className="px-2 py-2 text-right font-medium" title="Over-Dispatch Buffer expressed as % of Order Qty. Technical safety net for over-production tolerance — NOT a target the customer expects.">Buffer %</th>
                    <th className="px-2 py-2 text-right font-medium bg-pink-500/10">Total Qty</th>
                    <th className="px-2 py-2 text-right font-medium bg-emerald-500/10">Disp Qty</th>
                    <th className="px-2 py-2 text-right font-medium bg-amber-500/10">Pending</th>
                    <th className="px-2 py-2 text-right font-medium">Rate</th>
                    <th className="px-2 py-2 text-right font-medium w-28">Value</th>
                    <th className="px-2 py-2 text-left font-medium w-32">Delivery Date</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((li, i) => {
                    const tot = total_qty(li);
                    const pen = pending_qty(li);
                    const val = value_of(li);
                    return (
                      <tr key={li.id || li.item_no || `li-${i}`} className="border-t border-[var(--border)]">
                        <td className="px-1 py-1">
                          <div data-testid={`line-item-no-${i}`} className="h-8 px-2 flex items-center bg-[var(--bg-2)] border border-[var(--border)] rounded-sm font-mono text-sm text-zinc-400 select-none" title="Auto-generated">
                            {li.item_no}
                          </div>
                        </td>
                        <td className="px-1 py-1 w-28"><input
                          data-testid={`line-style-no-${i}`}
                          value={li.style_no || ""}
                          onChange={e => updateLine(i, "style_no", e.target.value.slice(0, 12))}
                          maxLength={12}
                          readOnly={!!li.article_id || isAmending}
                          className={inp + " font-mono w-full"    + ((li.article_id || isAmending) ? " cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60" : "")}
                          title={isAmending
                            ? "Frozen · line items cannot be edited on an authorized order"
                            : (li.article_id
                              ? "Locked · Style No is pulled from the picked Product Code. Clear the Product Code to re-enter."
                              : "Max 12 characters · type to filter the Product Code dropdown to matching styles")}
                        /></td>
                        <td className="px-1 py-1" style={{ width: "380px", minWidth: "380px", maxWidth: "380px" }}>
                          {isAmending ? (
                            <div data-testid={`line-article-locked-${i}`} className={inp + " opacity-70 cursor-not-allowed flex items-center gap-2 font-mono text-sm"} title="Frozen on authorized order">
                              <LockIcon size={11} weight="bold" className="text-amber-400 shrink-0" />
                              <span className="truncate">{li.article_no || "—"}</span>
                            </div>
                          ) : (
                            <ArticleCombobox
                              testid={`line-article-${i}`}
                              articles={articles}
                              styleFilter={li.style_no || ""}
                              value={li.article_id}
                              onChange={(v) => updateLine(i, "article_id", v)}
                              onCreated={(art) => { setArticles(prev => [...prev, art]); updateLine(i, "article_id", art.id); }}
                            />
                          )}
                        </td>
                        {/* HS Code — auto-pulled from Product Master (matched
                            by article_no == item.description|code). Read-only
                            here; edit in Master Data → Product Master to
                            change it globally. Feb 2026. */}
                        <td className="px-1 py-1 w-28">
                          {(() => {
                            const m = (items || []).find(x => (x.description || "") === li.article_no || (x.code || "") === li.article_no || x.id === li.item_id);
                            const hs = (m && m.hs_code) || li.hs_code || "";
                            return (
                              <div
                                data-testid={`line-hs-code-${i}`}
                                className="h-8 px-2 flex items-center bg-[var(--bg-2)] border border-[var(--border)] rounded-sm font-mono text-[11px] text-zinc-400 select-none truncate"
                                title={hs ? `HS Code · pulled from Product Master${m ? ` (${m.code || m.description})` : ""}` : "Pick a Product Code to auto-fill · set HS Code on Product Master to override"}
                              >
                                {hs || <span className="text-zinc-600">—</span>}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-1 py-1 w-32"><FormattedNumberInput step="1" max="9999999" data-testid={`line-order-qty-${i}`} value={li.order_qty} readOnly={isAmending} onChange={e => updateLine(i, "order_qty", Math.min(9999999, Number(e.target.value) || 0))} className={inpNum + " tabular-nums pr-1" + (isAmending ? " cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60" : "")} title={isAmending ? "Frozen on authorized order — use Hold/Cancel + new SO to change qty" : "Max 7 digits · 0 – 9,999,999"} /></td>
                        <td className="px-1 py-1 w-24">
                          <FormattedNumberInput
                            data-testid={`line-extra-pct-${i}`}
                            step="0.01" max="100"
                            value={li.extra_pct ?? 0}
                            readOnly={isAmending}
                            onChange={e => updateLine(i, "extra_pct", Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                            className={inpNum + " tabular-nums pr-1" + (isAmending ? " cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60" : "")}
                            title={isAmending ? "Frozen on authorized order" : "Over-Dispatch Buffer as % of Order Qty (0-100). Technical over-production tolerance, not a customer target."}
                          />
                          <div className="text-[9px] text-zinc-500 text-right font-mono mt-0.5 tabular-nums" title="Computed = Order Qty × Buffer %">
                            = {fmtQtyInt(Number(li.extra_qty) || 0)}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right font-mono bg-pink-500/5 tabular-nums w-28">{fmtQtyInt(tot)}</td>
                        <td className="px-1 py-1 w-32"><FormattedNumberInput step="1" max="9999999" data-testid={`line-disp-qty-${i}`} value={li.disp_qty} readOnly title="Dispatched qty is auto-posted only when a Delivery Challan is marked Delivered · capped at 7 digits" className={inpNum + " tabular-nums pr-1 cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60"} /></td>
                        <td className={`px-2 py-2 text-right font-mono tabular-nums w-28 ${pen > 0 ? "text-amber-400" : "text-emerald-400"} bg-amber-500/5`} title="Pending = Total − Dispatched · derived from 7-digit qty fields">{fmtQtyInt(Math.min(9999999, pen))}</td>
                        <td className="px-1 py-1 w-24"><FormattedNumberInput step="0.01" value={li.rate} readOnly={isAmending} onChange={e => updateLine(i, "rate", e.target.value)} className={inpNum + (isAmending ? " cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60" : "")} title={isAmending ? "Frozen on authorized order" : ""} /></td>
                        <td className="px-2 py-2 text-right font-mono w-28 truncate">{fmtInr(val, {symbol: false})}</td>
                        <td className="px-1 py-1 w-32"><input type="date" data-testid={`line-delivery-${i}`} value={li.delivery_date || ""} readOnly={isAmending} onChange={e => updateLine(i, "delivery_date", e.target.value)} className={inp + (isAmending ? " cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60" : "")} title={isAmending ? "Per-line delivery is frozen — use the top-level Delivery Date field to reschedule" : "Optional — falls back to the order-level delivery date"} /></td>
                        <td className="px-1 py-1 text-center">
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={() => openTracePdf(li.line_id, form.order_no)}
                              disabled={!li.line_id}
                              data-testid={`trace-line-${i}`}
                              title={li.line_id ? "Open line-trace PDF in a new tab" : "Save the SO once so this line gets a stable trace ID"}
                              className="p-1 rounded-sm hover:bg-blue-500/10 text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed"
                            ><FileTextIcon size={12} /></button>
                            {!isAmending && (
                              <button onClick={() => removeLine(i)} data-testid={`remove-line-${i}`} className="p-1 rounded-sm hover:bg-red-500/10 text-red-400"><TrashIcon size={12} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[var(--bg-2)] font-medium">
                    <td colSpan={6} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Totals</td>
                    <td className="px-2 py-2 text-right font-mono bg-pink-500/10">{fmtQtyInt(orderTotals(form.items).q)}</td>
                    <td className="px-2 py-2 text-right font-mono text-emerald-400">{fmtQtyInt(orderTotals(form.items).d)}</td>
                    <td className="px-2 py-2 text-right font-mono text-amber-400">{fmtQtyInt(orderTotals(form.items).p)}</td>
                    <td></td>
                    <td className="px-2 py-2 text-right font-mono">{fmtInr(orderTotals(form.items).v)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Optional linked master item picker — wraps so 9+ items don't
                blow out the dialog width (Feb 2026 · fix per user report). */}
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-zinc-500">
              <span className="overline">Optional</span>
              <span>Link a line to a master Item for auto-fill:</span>
              {form.items.map((li, i) => (
                <div key={li.id || li.item_no || `line-${i}`} className="flex items-center gap-1">
                  <span className="font-mono">#{li.item_no || i + 1}</span>
                  <Select value={li.item_id || ""} onValueChange={v => updateLine(i, "item_id", v)}>
                    <SelectTrigger className={selCls + " w-40"}><SelectValue placeholder="Master item" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {items.map(it => <SelectItem key={it.id} value={it.id}>{it.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => { setOpen(false); setIsAmending(false); setAmendReason(""); }} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-order" disabled={saving} onClick={() => save(false)} className="disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-sm font-semibold">
              {editing && isAmending ? "Save Amendment" : "Save as Draft"}
            </button>
            {!isAmending && canSubmitDoc("SO") && (!editing || ["", "Not Submitted", "Draft", "Rejected"].includes((editing.approval_status || "").trim())) && (
              <button data-testid="submit-for-authorisation" disabled={saving} onClick={() => save(true)} className="disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-sm bg-[var(--accent)] hover:opacity-90 text-sm font-semibold text-white">
                {(editing && (editing.approval_status || "").trim() === "Rejected") ? "Resubmit for Authorisation" : "Submit for Authorisation"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}

      {/* Amendment History Dialog */}
      <AdminAmendDialog open={!!adminAmend} orderType="SO" orderId={adminAmend?.id} onClose={() => setAdminAmend(null)} onSaved={() => load()} />
      <HoldReasonDialog open={!!holdAction} onClose={() => setHoldAction(null)}
        title={holdAction?.mode === "release" ? "Release Hold" : "Put Sales Order on Hold"}
        actionLabel={holdAction?.mode === "release" ? "Release Hold" : "Put on Hold"}
        endpoint={holdAction ? `/sales-orders/${holdAction.id}/${holdAction.mode === "release" ? "release-hold" : "hold"}` : ""}
        onDone={() => load()} />
      <Dialog open={!!historyOrder} onOpenChange={(o) => !o && setHistoryOrder(null)}>        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog" description="Complete change history for this Sales Order including edits, approvals and dispatches.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClockCounterClockwiseIcon size={18} weight="bold" />
              Amendment History — {historyOrder?.order_no}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {historyOrder?.authorized && (
              <div className="p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/20">
                <div className="flex items-center gap-2">
                  <LockIcon size={14} weight="bold" className="text-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-400">Authorised</span>
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  by <span className="font-mono">{historyOrder.authorized_by}</span> on <span className="font-mono">{fmtDateTimeIST(historyOrder.authorized_at)}</span>
                </div>
              </div>
            )}
            {(historyOrder?.amendments || []).length === 0 && <div className="text-sm text-zinc-500 text-center py-8">No amendments recorded.</div>}
            {(historyOrder?.amendments || []).slice().reverse().map((a) => (
              <div key={a.amendment_no || a.date} className="p-3 rounded-sm bg-[var(--bg-2)] border border-[var(--border)]" data-testid={`amendment-${a.amendment_no}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-semibold text-amber-400">{a.amendment_no}</span>
                  <span className="text-[10px] font-mono text-zinc-500">{fmtDateTimeIST(a.date)}</span>
                </div>
                <div className="text-xs text-zinc-400 mb-1"><span className="text-zinc-500">By:</span> {a.by}</div>
                <div className="text-sm mb-2"><span className="text-zinc-500 text-xs">Reason:</span> {a.reason}</div>
                {a.changed_fields?.length > 0 && (
                  <div className="text-xs">
                    <div className="text-zinc-500 mb-1">Changed fields:</div>
                    <div className="flex flex-wrap gap-1">
                      {a.changed_fields.map(f => (
                        <span key={f} className="px-1.5 py-0.5 rounded-sm bg-[var(--bg-0)] font-mono text-[10px] text-zinc-300">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setHistoryOrder(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* View Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="so-preview-dialog" description="Read-only preview of the Sales Order print — verify totals, delivery date and line items before sending.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>Sales Order — {preview?.order_no}</span>
              {preview?.order_against === "stock" && (<><span className="text-zinc-500">·</span><StockBadge testId="so-preview-stock-badge" /></>)}
              {preview && (
                <span data-testid="so-preview-fulfilment" className="px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-[var(--bg-2)] text-zinc-300">{FULFILMENT_LABEL[fulfilmentOf(preview)]}</span>
              )}
              {preview?.authorized && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-emerald-500/10 text-emerald-400">
                  <LockIcon size={10} weight="bold" />AUTHORISED
                </span>
              )}
              {/* P1-SO-MANAGEMENT-AUTH-CONTROL · Phase 2B · UAT-01 Issue 2 ·
                  Feb 2026 · Canonical Management Authorisation state badge
                  in View mode. Mirrors the row-level chip so the user sees
                  the same state whichever surface they open. */}
              {(preview?.approval_status || "").trim() === "Pending Approval" && (
                <span data-testid={`so-preview-waiting-auth-${preview?.order_no}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-amber-500/15 text-amber-400 border border-amber-500/30 uppercase tracking-widest">
                  <ClockCounterClockwiseIcon size={10} weight="bold" />Pending Authorisation
                </span>
              )}
              {(preview?.approval_status || "").trim() === "Rejected" && (
                <span data-testid={`so-preview-rejected-${preview?.order_no}`} title={preview?.rejection_comments || ""} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-red-500/15 text-red-400 border border-red-500/30 uppercase tracking-widest">
                  Rejected
                </span>
              )}
              {/* P1-SO Phase 2B · UAT-02 · Feb 2026 — amendment-Draft
                  state chip. Same positive-provenance predicate as row
                  status column + backend guard. */}
              {(preview?.approval_status || "").trim() === "Draft" && (preview?.approval_history || []).some(e => e?.action === "Draft" && e?.prev_status === "Authorized") && (
                <span data-testid={`so-preview-reauth-${preview?.order_no}`} title="Amendment saved — awaiting Management Re-authorisation" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-purple-500/15 text-purple-300 border border-purple-500/30 uppercase tracking-widest">
                  Awaiting Re-authorisation
                </span>
              )}
              {(preview?.approval_status || "").trim() === "Not Submitted" && !preview?.authorized && (
                <span data-testid={`so-preview-not-submitted-${preview?.order_no}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-zinc-500/15 text-zinc-300 border border-zinc-500/30 uppercase tracking-widest">
                  Draft
                </span>
              )}
              <StatusBadge status={preview?.status} />
              <StatusBadge status={preview?.priority} />
            </DialogTitle>
          </DialogHeader>
          {preview && (() => {
            const t = orderTotals(preview.items || []);
            // R2-01L A4-02 · G6/D3 · commercial value excludes buffer and cancelled quantity.
            const netValue = (li) => Math.max(0, (Number(li.order_qty) || 0) - (Number(li.cancelled_qty) || 0)) * (Number(li.rate) || 0);
            const netTotal = (preview.items || []).reduce((a, li) => a + netValue(li), 0);
            const soCancelled = isOrderCancelled(preview);
            const cust = customers.find(c => c.id === preview.customer_id) || {};
            const addrs = Array.isArray(cust?.addresses) ? cust.addresses : [];
            // Same resolver used by printSo — prefer the stored snapshot
            // so preview & PDF always agree on what will get printed.
            const _resolve = (snap, id, prefer) => {
              const s = snap && Object.keys(snap || {}).length ? snap : null;
              if (s) return s;
              const byId = addrs.find(a => a.id === id);
              if (byId) return byId;
              return addrs.find(a => a[prefer]) || addrs[0] || {};
            };
            const billAddr = _resolve(preview.bill_to_snapshot, preview.bill_to_address_id, "is_primary_bill");
            const shipAddr = _resolve(preview.ship_to_snapshot, preview.ship_to_address_id, "is_primary_ship");
            const AddrCard = ({ title, a, testId }) => {
              const lbl = a?.label === "Other" ? (a?.custom_label || "Other") : (a?.label || "");
              return (
                <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/40" data-testid={testId}>
                  <div className="overline text-[10px] mb-1 flex items-center gap-2">
                    {title}
                    {lbl && <span className="inline-flex px-1.5 py-0.5 rounded-sm border border-[var(--accent)]/40 text-[var(--accent)] bg-[var(--accent)]/5 text-[9px] font-mono uppercase tracking-widest">{lbl}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-300 whitespace-pre-line leading-snug">
                    {a?.address || "—"}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1">
                    {[a?.city, a?.state, a?.pincode].filter(Boolean).join(", ") || "—"}
                    {a?.country ? ` · ${a.country}` : ""}
                  </div>
                  {a?.gstin && <div className="text-[11px] text-zinc-500 mt-1"><span className="text-zinc-500">GSTIN:</span> <span className="text-white font-mono">{a.gstin}</span></div>}
                  {(a?.contact_person || a?.phone) && (
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {a.contact_person || ""}{a.phone ? ` · ${a.phone}` : ""}
                    </div>
                  )}
                </div>
              );
            };
            return (
              <>
                <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="overline text-[10px] mb-0.5">Customer Name</div>
                    <div className="font-semibold text-sm" data-testid="so-preview-customer-name">{preview?.order_against === "stock" ? "Stock" : (cust.name || "—")}</div>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                    <div className="font-mono text-sm">{preview.po_number || "—"}</div>
                  </div>
                </div>

                {/* Feb 2026 · Bill To / Ship To full-address preview */}
                {preview.order_against !== "stock" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <AddrCard title="Bill To" a={billAddr} testId="so-preview-bill-to" />
                    <AddrCard title="Ship To" a={shipAddr} testId="so-preview-ship-to" />
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3 text-xs mb-4">
                  <div><div className="overline text-[10px] mb-0.5">Order Date</div><div className="font-mono">{preview.order_date}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Delivery Date</div><div className="font-mono">{preview.delivery_date}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Total Qty (m) · incl. buffer</div><div className="font-mono font-semibold">{fmtQtyInt(t.q)}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Total Value (₹)</div><div className="font-mono font-semibold" data-testid="so-preview-total-value" title="(Order Qty − Cancelled) × Rate · buffer excluded">{fmtInr(netTotal)}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Line Items</div><div className="font-mono">{(preview.items || []).length}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Amendments</div><div className="font-mono">{(preview.amendments || []).length}</div></div>
                </div>

                <DocumentInfo docNo={preview.order_no} createdAt={preview.created_at} updatedAt={preview.updated_at} createdBy={preview.created_by} docLabel="Sales Order No." testidPrefix="so" />

                <div className="overline mb-2">Line Items</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--bg-2)]">
                      <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Product Code</th>
                        <th className="px-2 py-2 text-left">Style No</th>
                        <th className="px-2 py-2 text-right">Order Qty</th>
                        <th className="px-2 py-2 text-right" title="Over-Dispatch Buffer — technical over-production cap, not the customer target">Buffer</th>
                        <th className="px-2 py-2 text-right" title="Order Qty + Buffer — technical dispatch cap">Total · incl. buffer</th>
                        <th className="px-2 py-2 text-right">Rate</th>
                        <th className="px-2 py-2 text-right" title="(Order Qty − Cancelled) × Rate · buffer excluded">Value</th>
                        <th className="px-2 py-2 text-left" title="Item-wise Required Delivery Date">Delivery</th>
                        {canQtyView && preview?.authorized && <th className="px-2 py-2 text-left" title="Read-only quantity breakdown">Qty Breakdown</th>}
                        {/* Feb 27, 2026 · Per-line status + Actions */}
                        <th className="px-2 py-2 text-center">Status</th>
                        {preview?.authorized && canAuthorize && <th className="px-2 py-2 text-center w-32">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.items || []).map((li, i) => {
                        const q = total_qty(li);
                        // Feb 27, 2026 · Per-line status classifier.
                        // R2-01L A4-02 · G6/D3 · completion uses Order Qty (buffer excluded) · G1 cancelled order.
                        const disp = Number(li.disp_qty || li.produced || 0);
                        const cancelled = Number(li.cancelled_qty || 0);
                        const pending = soCancelled ? 0 : Math.max(0, (Number(li.order_qty) || 0) - cancelled - disp);
                        const qp = qpos[li.line_id];
                        const prot = qp?.readiness_block || null;
                        let st = "In-Progress";
                        let cls = "border-sky-500/50 text-sky-300 bg-sky-500/10";
                        if (soCancelled) { st = "Cancelled"; cls = "border-red-500/50 text-red-300 bg-red-500/10"; }
                        else if (li.short_closed) { st = "Closed";   cls = "border-zinc-500/50 text-zinc-300 bg-zinc-500/10 [border-style:dashed]"; }
                        else if (pending <= 0 && disp > 0) { st = "Completed"; cls = "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"; }
                        else if (li.amended) { st = "Amended"; cls = "border-amber-500/50 text-amber-300 bg-amber-500/10"; }
                        // Feb 07, 2026 · Line-Order-Qty editor now gated on
                        // `canEdit` (`sales_orders:edit`) not `canAuthorize`
                        // — amending a line's quantity is an "edit", not an
                        // approval action. Server side is patched to match.
                        const canEditLine = preview?.authorized && canEdit && !soCancelled && !li.short_closed && pending > 0;
                        return (
                          <tr key={li.id || li.line_id || li.item_no || `li-${i}`}
                              data-testid={`so-line-${li.line_id || i}`}
                              className="border-t border-[var(--border)]">
                            <td className="px-2 py-1 font-mono text-center">{li.item_no || String(i + 1).padStart(3, "0")}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{li.article_no || "—"}</td>
                            <td className="px-2 py-1 font-mono text-[11px]" data-testid={`so-preview-line-style-${i}`}>{li.style_no || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono">{fmtQtyInt(li.order_qty)}</td>
                            <td className="px-2 py-1 text-right font-mono">{(Number(li.extra_qty) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                            <td className="px-2 py-1 text-right font-mono">{fmtQtyInt(q)}</td>
                            <td className="px-2 py-1 text-right font-mono">{fmtInr(li.rate)}</td>
                            <td className="px-2 py-1 text-right font-mono" data-testid={`so-line-value-${li.line_id || i}`}>{fmtInr(netValue(li))}</td>
                            <td className="px-2 py-1 font-mono text-zinc-300 text-[11px]" data-testid={`so-line-delivery-${li.line_id || i}`}>{li.delivery_date || preview.delivery_date || "—"}</td>
                            {canQtyView && preview?.authorized && (
                              <td className="px-2 py-1"><LineQtyBreakdown line={qp} cancelled={soCancelled} testid={`so-line-breakdown-${li.line_id || i}`} /></td>
                            )}
                            <td className="px-2 py-1 text-center">
                              <div className="inline-flex items-center gap-1">
                                <span data-testid={`so-line-status-${li.line_id || i}`}
                                      className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest ${cls}`}
                                      title={li.short_close_reason || (li.line_history || []).slice(-1)[0]?.reason || ""}>
                                  {st}
                                </span>
                                <ProtectionBadge cls={prot} testid={`so-line-protection-${li.line_id || i}`} />
                                <button data-testid={`so-line-history-${li.line_id || i}`}
                                  onClick={(e) => { e.stopPropagation(); setHistoryLine({ source: "SO", orderId: preview.id, lineId: li.line_id, lineRef: li.line_id, orderNo: preview.order_no, productCode: li.article_no }); }}
                                  title="View line audit timeline"
                                  className="h-5 px-1 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--accent)] rounded-sm text-[9px] font-mono uppercase tracking-widest">
                                  History
                                </button>
                                {preview?.authorized && canQtyView && li.line_id && (
                                  <LineQtyActions otype="so" orderId={preview.id} lineRef={li.line_id} articleNo={li.article_no}
                                    testid={`so-line-qty-${li.line_id}`} readOnly={user?.role !== "Administrator"}
                                    onChanged={async () => { const { data: fresh } = await api.get(`/sales-orders/${preview.id}`); if (fresh) setPreview(fresh); load(); }} />
                                )}
                              </div>
                            </td>
                            {preview?.authorized && canAuthorize && (
                              <td className="px-2 py-1 text-center">
                                {canEditLine && (
                                  <div className="flex items-center justify-center gap-1">
                                    <button data-testid={`so-line-close-${li.line_id}`}
                                      disabled={!!prot}
                                      onClick={async () => {
                                        if (prot) return;
                                        const reason = window.prompt(`Short-Close Line ${li.item_no || i+1}?\nPending ${pending} will be cancelled, other lines stay active.\nReason (min 4 chars):`, "");
                                        if (reason === null) return;
                                        if ((reason || "").trim().length < 4) { toast.error("Reason must be at least 4 characters"); return; }
                                        try {
                                          const { data } = await api.post(`/sales-orders/${preview.id}/lines/${li.line_id}/short-close`, { reason: reason.trim() });
                                          toast.success(`Line ${li.item_no || i+1} short-closed · ${data.closed_qty} m cancelled`);
                                          const { data: fresh } = await api.get(`/sales-orders/${preview.id}`);
                                          if (fresh) setPreview(fresh);
                                          load();
                                        } catch (e) { toast.error(e?.response?.data?.detail || "Short-close failed"); }
                                      }}
                                      title={prot ? `Close disabled · ${PROTECTION_TEXT[prot] || prot}` : "Zero this line's pending qty (with reason)"}
                                      className="h-6 px-1.5 border border-red-500/50 text-red-300 hover:bg-red-500/10 rounded-sm text-[9px] font-mono uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                                      Close
                                    </button>
                                  </div>
                                )}
                                {!canEditLine && <span className="text-[9px] text-zinc-600">—</span>}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-[var(--bg-2)]">
                      <tr className="font-semibold border-t border-[var(--border)]">
                        <td colSpan={4} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Totals</td>
                        <td className="px-2 py-2 text-right font-mono">{fmtQtyInt(t.q)}</td>
                        <td></td>
                        <td className="px-2 py-2 text-right font-mono">{fmtInr(netTotal)}</td>
                        <td colSpan={(preview?.authorized && canAuthorize ? 2 : 1) + (canQtyView && preview?.authorized ? 1 : 0)}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {preview.special_instructions && (
                  <div className="mt-3 text-xs"><span className="overline">Special Instructions:</span> <span className="text-zinc-400">{preview.special_instructions}</span></div>
                )}
              </>
            );
          })()}
          <DialogFooter>
            {/* P1-SO-MANAGEMENT-AUTH-CONTROL · Phase 2B · UAT-01 Issue 1 ·
                Feb 2026 — canonical Submit / Resubmit lifecycle action
                surfaced in View mode. Uses the SAME `submitForApproval`
                handler that the row-action button calls (no second
                workflow). Visible only in Not Submitted / Rejected
                states — hidden in Pending Approval / Authorised /
                Cancelled per the sealed lifecycle. Not gated on
                `downstreamLock` because Submit does not mutate lines
                or downstream artefacts. */}
            {preview && !preview.authorized && canSubmitDoc("SO") && (preview.status || "").toLowerCase() !== "cancelled" && (() => {
              const appr = (preview.approval_status || "").trim();
              const isNotSubmitted = appr === "" || appr === "Not Submitted";
              const isRejected = appr === "Rejected";
              // P1-SO Phase 2B · UAT-02 · Feb 2026 — amendment-Draft eligibility.
              const isReauth = appr === "Draft" && (preview.approval_history || []).some(
                (e) => e?.action === "Draft" && e?.prev_status === "Authorized",
              );
              if (!(isNotSubmitted || isRejected || isReauth)) return null;
              const label = isReauth ? "Submit Amendment for Authorisation" : (isRejected ? "Resubmit for Authorisation" : "Submit for Authorisation");
              return (
                <button
                  data-testid={`preview-submit-for-approval-${preview.order_no}`}
                  onClick={async () => { await submitForApproval(preview); setPreview(null); }}
                  className="px-4 py-2 rounded-sm border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 text-sm font-semibold flex items-center gap-2 mr-auto"
                ><PaperPlaneTiltIcon size={14} weight="bold" />{label}</button>
              );
            })()}
            {/* Feb 2026 · Modify Order — replaces the legacy Unauthorize button.
                Opens the shared ModifyDocumentPopover that probes downstream
                docs and either lets an Admin Unapprove (when nothing is
                linked) or Short Close / Cancel Balance per line (when there
                are Work Orders / Packing Slips / DCs). */}
            {/* IMP-1C · P-2 — host follows action-specific permissions + backend capabilities. */}
            {((preview?.authorized && (canSoClose || canSoUnapprove)) || ((previewCtl?.legacy_actions || previewCtl?.can_reverse_short_close) && canSoClose)) && (
              <button
                data-testid={`modify-so-${preview.order_no}`}
                onClick={() => { setModifyTarget(preview); setModifyOpen(true); }}
                className="px-4 py-2 rounded-sm border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-sm font-semibold mr-auto"
              >Modify Order</button>
            )}
            {/* IMP-1C · P-1 — reference-only Administrative Amendment for a verified legacy Pending SO. */}
            {previewCtl?.legacy_actions && previewCtl?.can_admin_amend && canEdit && (
              <button
                data-testid={`admin-amend-view-${preview.order_no}`}
                onClick={() => setAdminAmend({ id: preview.id })}
                className="px-4 py-2 rounded-sm border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-sm font-semibold"
              >Administrative Amendment…</button>
            )}
            {/* Phase 2C-6B1A · Raise Amendment — distinct control from Modify
                Order; reuses the controlled Edit-in-amend-mode (openEdit) and
                follows the existing authorization/downstream/capability rules
                (NOT a commercial-freeze bypass). */}
            {preview?.authorized && (preview?.status || "").toLowerCase() !== "cancelled" && (preview?.approval_status || "").trim() !== "Pending Approval" && hasPerm("sales_orders:edit") && (
              <button
                data-testid={`amend-so-${preview.order_no}`}
                onClick={() => { const p = preview; setPreview(null); openEdit(p); }}
                className="px-4 py-2 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-sm font-semibold"
              >Raise Amendment</button>
            )}
            {/* Phase 2C-6B1A · Hold / Release Hold — moved from the SO row. */}
            {preview?.authorized && (preview?.status || "").toLowerCase() !== "cancelled" && hasPerm("sales_orders:edit") && ["open", "planned", "running"].includes((preview?.status || "").toLowerCase()) && (
              <button
                data-testid={`hold-so-${preview.order_no}`}
                onClick={() => setHoldAction({ id: preview.id, mode: "hold" })}
                className="px-4 py-2 rounded-sm border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 text-sm font-semibold"
              >Hold Order</button>
            )}
            {(preview?.status || "").toLowerCase() === "hold" && hasPerm("sales_orders:edit") && (
              <button
                data-testid={`release-hold-so-${preview.order_no}`}
                onClick={() => setHoldAction({ id: preview.id, mode: "release" })}
                className="px-4 py-2 rounded-sm border border-green-500/40 text-green-400 hover:bg-green-500/10 text-sm font-semibold"
              >Release Hold</button>
            )}
            {/* Phase 2C-6B1A · Cancel Order (terminal, danger-styled). Authorised
                → controlled /cancel; Draft → reasoned withdraw. Reason +
                confirmation preserved by the underlying dialogs. */}
            {preview?.authorized && (preview?.status || "").toLowerCase() !== "cancelled" && hasPerm("sales_orders:delete") && (
              <button
                data-testid={`cancel-order-so-${preview.order_no}`}
                onClick={() => cancelOrder(preview.id, preview.order_no)}
                className="px-4 py-2 rounded-sm border border-red-500/50 text-red-400 hover:bg-red-500/10 text-sm font-semibold"
              >Cancel Order</button>
            )}
            {preview && !preview.authorized && (preview.approval_status || "").trim() !== "Pending Approval" && !downstreamLocks[preview.id] && (preview.status || "").toLowerCase() !== "cancelled" && hasPerm("sales_orders:delete") && (
              <button
                data-testid={`cancel-draft-so-${preview.order_no}`}
                onClick={() => remove(preview.id, preview.order_no)}
                className="px-4 py-2 rounded-sm border border-red-500/50 text-red-400 hover:bg-red-500/10 text-sm font-semibold"
              >Cancel Order</button>
            )}
            <button onClick={() => preview && printSo(preview)} data-testid="preview-print-so" className="px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print / PDF</button>
            {preview?.id && <DocDownloadButtons type="SO" id={preview.id} testPrefix="so-preview" />}
            <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
      <LineHistoryDrawer open={!!historyLine} onClose={() => setHistoryLine(null)}
        source={historyLine?.source} orderId={historyLine?.orderId} lineId={historyLine?.lineId} lineRef={historyLine?.lineRef}
        orderNo={historyLine?.orderNo} productCode={historyLine?.productCode} />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · Dynamic Order Modification popover — shared across SO/
          ESO/PO/WO. Feb 27, 2026 · Now driven by `modifyTarget` so it
          works both from the preview-modal footer AND directly from the
          row-level Modify / Short Close button. */}
      {modifyTarget && (
        <ModifyDocumentPopover
          docType="SO"
          docId={modifyTarget.id}
          docNo={modifyTarget.order_no}
          open={modifyOpen}
          onClose={() => { setModifyOpen(false); setModifyTarget(null); }}
          onDone={async () => { setPreview(null); setModifyOpen(false); setModifyTarget(null); await load(); }}
          onLineCancel={async () => { await load(); }}
          testidPrefix="so-modify"
          allow={{ unapprove: canSoUnapprove, close: canSoClose }}
        />
      )}
      {/* PHASE-2 · Controlled Cancel Order (authorised SO) — reason required,
          irreversible; backend flips status=Cancelled and preserves history. */}
      <CancelWithReasonDialog
        open={!!cancelOrderTarget}
        onOpenChange={(o) => { if (!o) setCancelOrderTarget(null); }}
        docType="SO"
        docNo={cancelOrderTarget?.order_no}
        cancelPath={cancelOrderTarget ? `/sales-orders/${cancelOrderTarget.id}/cancel` : ""}
        onDone={async () => { setCancelOrderTarget(null); await load(); }}
      />
    </div>
  );
}

function F({ label, children, className = "" }) {  return (
    <div className={className}>
      <div className="overline mb-1.5">{label}</div>
      {children}
    </div>  );
}

// Feb 2026 · Full-address editor block used under the Bill To / Ship To
// picker dropdowns. Renders the resolved snapshot (or the picked address
// if the snapshot is empty) as editable fields so users can hand-tweak
// a one-time shipping location without editing the Customer Master.
// The parent owns the snapshot object; onChange returns a shallow patch.
function SoAddressBlock({ title, testId, snapshot, fallback, readOnly, onChange, onCopyFromBill }) {
  // Prefer the persisted snapshot; when empty, transparently fall back to
  // the currently-picked address so the block never looks blank.
  const effective = React.useMemo(() => {
    const s = snapshot && Object.keys(snapshot || {}).length ? snapshot : null;
    return s || (fallback ? fallback() : {}) || {};
  }, [snapshot, fallback]);
  const inpCls = "w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs";
  const areaCls = "w-full min-h-[48px] px-1.5 py-1 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs resize-y";
  const displayLabel = effective?.label === "Other" ? (effective?.custom_label || "Other") : (effective?.label || "");
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/40 space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
          {title}
          {displayLabel && <span className="px-1.5 py-0.5 rounded-sm border border-[var(--accent)]/40 text-[var(--accent)] bg-[var(--accent)]/5 text-[9px] uppercase tracking-widest">{displayLabel}</span>}
        </div>
        {!readOnly && onCopyFromBill && (
          <button
            type="button"
            onClick={onCopyFromBill}
            data-testid={`${testId}-copy-from-bill`}
            className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
          >
            Same as Bill To
          </button>
        )}
      </div>
      <textarea
        disabled={readOnly}
        value={effective.address || ""}
        onChange={e => onChange({ address: e.target.value })}
        placeholder="Street / Building"
        className={areaCls}
        data-testid={`${testId}-address`}
      />
      <div className="grid grid-cols-3 gap-1.5">
        <input disabled={readOnly} value={effective.city || ""} onChange={e => onChange({ city: e.target.value })} placeholder="City" className={inpCls} data-testid={`${testId}-city`} />
        <input disabled={readOnly} value={effective.state || ""} onChange={e => onChange({ state: e.target.value })} placeholder="State" className={inpCls} data-testid={`${testId}-state`} />
        <input disabled={readOnly} value={effective.pincode || ""} onChange={e => onChange({ pincode: e.target.value })} placeholder="Zip / PIN" className={`${inpCls} font-mono`} data-testid={`${testId}-pincode`} />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <input disabled={readOnly} value={effective.country || ""} onChange={e => onChange({ country: e.target.value })} placeholder="Country" className={inpCls} data-testid={`${testId}-country`} />
        <input disabled={readOnly} value={effective.gstin || ""} onChange={e => onChange({ gstin: e.target.value.toUpperCase() })} placeholder="GSTIN" className={`${inpCls} font-mono uppercase`} data-testid={`${testId}-gstin`} />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <input disabled={readOnly} value={effective.contact_person || ""} onChange={e => onChange({ contact_person: e.target.value })} placeholder="Contact person" className={inpCls} data-testid={`${testId}-contact`} />
        <input disabled={readOnly} value={effective.phone || ""} onChange={e => onChange({ phone: e.target.value })} placeholder="Phone" className={inpCls} data-testid={`${testId}-phone`} />
      </div>
      <div className="text-[10px] text-zinc-500 italic">Edit any field to override just for this order — the Customer Master stays untouched.</div>
    </div>
  );
}


function CustomerCombobox({ customers, value, onChange, onCreated }) {
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  // Feb 2026 · SO is a domestic document — only Indian buyers may be
  // picked. Legacy rows without a `country` value are treated as India
  // (matches the backend rule so we don't hide existing customers).
  const isIndian = (c) => {
    const cc = (c?.country || "").trim().toUpperCase();
    return !cc || cc === "INDIA";
  };
  const indianCustomers = React.useMemo(() => customers.filter(isIndian), [customers]);
  const nonIndianCount = customers.length - indianCustomers.length;
  const emptyCust = { code: "", name: "", contact_person: "", email: "", phone: "", country: "India" };
  const [draft, setDraft] = useState(emptyCust);

  const openAdd = (nameSeed = "") => {
    setDraft({ ...emptyCust, name: nameSeed });
    setShowAdd(true);
  };

  const saveNew = async () => {
    if (!draft.name.trim()) return toast.error("Customer name is required");
    if (!draft.code.trim()) return toast.error("Customer code is required");
    // Feb 2026 · SO domestic-only guard. Foreign buyers must be added
    // from the Exports module so they show up in ESO's picker instead.
    const cc = (draft.country || "").trim().toUpperCase();
    if (cc && cc !== "INDIA") {
      return toast.error(`SO buyers must be Indian. Add ${draft.name} from Exports for ESOs.`);
    }
    setCreating(true);
    try {
      const payload = { ...draft, country: draft.country?.trim() || "India" };
      const { data } = await api.post("/customers/", payload);
      toast.success(`Added ${data.name}`);
      onCreated(data);
      setShowAdd(false);
      setDraft(emptyCust);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add customer");
    } finally {
      setCreating(false);
    }
  };

  const inp2 = "h-9 w-full bg-[var(--bg-0)] border border-[var(--border)] px-3 rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

  // Sep 04, 2026 · Delegated the popover + search UX to the shared
  // SearchablePicker component. Foreign buyers stay hidden, add-new
  // dialog + india-only guard preserved via the footer slot.
  return (
    <>
      <SearchablePicker
        testid="select-customer"
        value={value || ""}
        onChange={onChange}
        placeholder="Select customer"
        groupHeading={`Indian Customers${nonIndianCount ? ` · ${nonIndianCount} foreign hidden` : ""}`}
        emptyLabel="No customer found."
        headerHint="Search by customer name or code"
        options={indianCustomers.map(c => ({
          value: c.id,
          code: c.code,
          name: c.name,
        }))}
        footer={({ query, close }) => (
          <button
            type="button"
            data-testid="customer-add-new-btn"
            onClick={() => { openAdd(query); close(); }}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[var(--accent)] hover:bg-[var(--bg-2)] rounded-sm"
          >
            <PlusIcon size={14} weight="bold" />
            Add new customer{query ? ` "${query}"` : ""}
          </button>
        )}
      />

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="customer-add-dialog" description="Add a new customer inline while drafting a Sales Order — captures name, GSTIN and default region.">
          <DialogHeader><DialogTitle className="font-display">New Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Code *"><input data-testid="cust-code" value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value.toUpperCase() })} className={inp2} placeholder="e.g. DECATHLON" /></F>
            <F label="Name *"><input data-testid="cust-name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className={inp2} placeholder="e.g. Decathlon India" /></F>
            <F label="Contact Person"><input data-testid="cust-contact" value={draft.contact_person} onChange={e => setDraft({ ...draft, contact_person: e.target.value })} className={inp2} /></F>
            <F label="Country (India only for SO)">
              <input
                data-testid="cust-country"
                value={draft.country}
                readOnly
                className={inp2 + " opacity-70 cursor-not-allowed bg-[var(--bg-2)]/60"}
                title="SO buyers are domestic. For foreign buyers, add them from the Exports module."
              />
            </F>
            <F label="Email"><input data-testid="cust-email" type="email" value={draft.email} onChange={e => setDraft({ ...draft, email: e.target.value })} className={inp2} /></F>
            <F label="Phone"><input data-testid="cust-phone" value={draft.phone} onChange={e => setDraft({ ...draft, phone: e.target.value })} className={inp2} /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="cust-save-btn" disabled={creating} onClick={saveNew} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white disabled:opacity-60">{creating ? "Saving…" : "Save Customer"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const COLS = {
  order_no:    { label: "Sales Order No",   align: "right",  render: ({ o, isFirst, lineIdx }) => (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1 justify-end">
      {/* SO Register exception · complete SO number on ONE line (no wrap /
          no ellipsis), repeated identically on every line-item row. UI-only —
          document number value & global DocumentNumberDisplay rule elsewhere
          are unchanged. */}
      <span
        data-testid={isFirst ? `so-no-${o.order_no}` : `so-no-${o.order_no}-${lineIdx}`}
        className="font-mono whitespace-nowrap text-white text-xs font-semibold"
      >{o.order_no}</span>
      {o.order_against === "stock" && (<><span className="text-zinc-500 text-xs">·</span><StockBadge testId={isFirst ? `so-stock-chip-${o.order_no}` : undefined} /></>)}
      </div>
      {isFirst && fulfilmentOf(o) === "sales_only" && (
        <span data-testid={`so-sales-only-chip-${o.order_no}`} className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-300 w-fit">SALES ONLY</span>
      )}
    </div>
  ) },
  sales_date:  { label: "Date",             align: "left",  render: ({ o }) => {
    // Compact dd-mm-yyyy display (no wrap). Stored value unchanged.
    const p = String(o.order_date || "").split("-");
    const disp = p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : (o.order_date || "—");
    return <span className="font-mono text-zinc-400 whitespace-nowrap">{disp}</span>;
  } },
  customer:    { label: "Customer",         align: "left",  width: "min-w-[150px] max-w-[190px]", render: ({ o, customerName }) => {
    const name = o.order_against === "stock" ? "Stock" : customerName(o.customer_id);  // STOCK-CUST-01
    return <span className="text-zinc-200 font-medium block leading-tight line-clamp-2" title={name} data-testid={`so-customer-cell-${o.order_no}`}>{name}</span>;
  } },
  item_no:     { label: "Item No",          align: "right", render: ({ line, lineIdx }) => line ? <span className="font-mono text-zinc-400">{line.item_no || String(lineIdx + 1).padStart(3, "0")}</span> : <span className="text-zinc-500">—</span> },
  po:          { label: "Customer PO No",   align: "left",  render: ({ o }) => <span className="font-mono text-zinc-400 whitespace-nowrap">{o.po_number || "—"}</span> },
  style_no:    { label: "Style No",         align: "left",  render: ({ line }) => line ? <span className="font-mono text-zinc-400">{line.style_no || "—"}</span> : <span className="text-zinc-500">—</span> },
  article_no:  { label: "Product Code",     align: "left",  sticky: true, width: "sticky left-0 z-20 bg-[var(--bg-1)]", render: ({ o, line, lineIdx, setArticleFilter }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const code = line.article_no || "—";
    // Mar 3, 2026 · Product Code MUST display in full — remove the
    // fixed max-w truncate so the column grows dynamically to fit the
    // longest code. `whitespace-nowrap` keeps it on one line so long
    // codes push the column wider instead of wrapping mid-string.
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setArticleFilter && setArticleFilter(line.article_no); }}
        data-testid={`article-chip-${o.order_no}-${lineIdx}`}
        title={`Filter by ${code}`}
        className="font-mono text-[var(--accent)] font-medium hover:underline text-left whitespace-nowrap text-xs"
      >
        {code}
      </button>
    );
  } },
  hs_code:     { label: "HS Code",          align: "left",  render: ({ line, items }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    // HS Code is authoritative on the Product Master (`items` collection).
    // Match by description == article_no (same convention used by Exports
    // and Packing Slips). Fall back to any snapshot stored on the SO line.
    const m = (items || []).find(x => (x.description || "") === line.article_no || (x.code || "") === line.article_no || x.id === line.item_id);
    const hs = (m && m.hs_code) || line.hs_code || "";
    return hs
      ? <span className="font-mono text-zinc-300 text-xs" data-testid={`hs-code-${line.article_no || ""}`}>{hs}</span>
      : <span className="text-zinc-500">—</span>;
  } },
  description: { label: "Description",      align: "left",  render: ({ line, articles }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    // Prefer article-master description; fallback to whatever's stored on the SO line.
    const art = (articles || []).find(a => a.id === line.article_id || a.product_code === line.article_no);
    const desc = art?.description || line.description || "—";
    return <span className="text-zinc-300 text-xs" title={desc}>{desc}</span>;
  } },
  colour:      { label: "Colour",           align: "left",  render: ({ line, articles }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const art = (articles || []).find(a => a.id === line.article_id || a.product_code === line.article_no);
    const c = art?.colour || line.colour || "—";
    return <span className="text-zinc-300 text-xs">{c}</span>;
  } },
  size:        { label: "Size",             align: "left",  render: ({ line, articles }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const art = (articles || []).find(a => a.id === line.article_id || a.product_code === line.article_no);
    const s = art?.size || line.size || "—";
    return <span className="font-mono text-zinc-300 text-xs">{s}</span>;
  } },
  order_qty:   { label: "Order Qty",        align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const qty = Number(line.order_qty) || 0;
    return (
      <span className="font-mono font-semibold text-white tabular-nums" data-testid={`article-order-qty-${o?.order_no}-${lineIdx}`} title="Order Qty · what the customer signed off on (the target)">
        {fmtQtyInt(qty)}
      </span>
    );
  } },
  // Sep 04, 2026 · Buffer Qty rendered as its own column so operators
  // read it as a *technical over-dispatch cap*, not a target. Value is
  // stored as `extra_qty` on the DB (kept for compatibility); label is
  // "Buffer Qty" everywhere in the UI.
  extra_qty: { label: "Buffer Qty", align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const buf = Number(line.extra_qty) || 0;
    if (!buf) return <span className="font-mono text-zinc-500 tabular-nums" data-testid={`article-buffer-qty-${o?.order_no}-${lineIdx}`}>0</span>;
    return (
      <span className="font-mono text-zinc-400 tabular-nums" data-testid={`article-buffer-qty-${o?.order_no}-${lineIdx}`} title="Buffer Qty · technical over-dispatch cap. NEVER counted in Pending math.">
        {fmtQtyInt(buf)}
      </span>
    );
  } },
  // Sep 04, 2026 · Total Qty (Order + Buffer) is now a *display-only*
  // column — kept visible for technical reference so operators can see
  // the physical hard-cap for a line, but it must NEVER feed the Pending
  // gauge. `lib_pending.get_line_pending` gates on order_qty alone.
  total_qty:   { label: "Total Qty",        align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const qty = Number(line.order_qty) || 0;
    const extra = Number(line.extra_qty) || 0;
    return (
      <span className="font-mono text-zinc-200 tabular-nums" data-testid={`article-total-qty-${o?.order_no}-${lineIdx}`} title="Total Qty = Order + Buffer · technical cap only, not the pending target.">
        {fmtQtyInt(qty + extra)}
      </span>
    );
  } },
  // Feb 28, 2026 · Dispatched + Pending qty exposed on the main SO grid so
  // ops can eyeball fulfilment without opening the Status Report tab.
  // disp_qty is auto-posted when a DC is marked Delivered.
  disp_qty:    { label: "Dispatch Qty",     align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const d = Number(line.disp_qty ?? line.produced) || 0;
    return (
      <span className="font-mono font-semibold text-emerald-300 tabular-nums" data-testid={`article-disp-qty-${o?.order_no}-${lineIdx}`} title="Dispatched via Delivery Challans marked Delivered">
        {fmtQtyInt(d)}
      </span>
    );
  } },
  pending_qty: { label: "Pending Qty",      align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    // Sep 04, 2026 · Single-source pending — mirrors backend
    // `lib_pending.get_line_pending`: max(0, order − cancelled − disp).
    // Short-closed lines force 0. Buffer NEVER enters the calc.
    const q = Number(line.order_qty) || 0;
    const c = Number(line.cancelled_qty) || 0;
    const d = Number(line.disp_qty ?? line.produced) || 0;
    // R2-01L A4-02 · G1 cancelled order → 0 · D4 unauthorised shown as Pending Approval (not actionable).
    const cancelledSo = isOrderCancelled(o);
    const p = line.short_closed || cancelledSo ? 0 : Math.max(0, q - c - d);
    const notActionable = !cancelledSo && !isActionablePending("so", o);
    const cls = notActionable ? "text-zinc-500" : p > 0 ? "text-amber-300" : "text-emerald-400";
    return (
      <span className={`font-mono font-semibold tabular-nums ${cls}`} data-testid={`article-pending-qty-${o?.order_no}-${lineIdx}`} title={cancelledSo ? "Order cancelled · no pending" : notActionable ? "Not actionable · order is not authorised (Pending Approval / Draft)" : "Pending = Order Qty − Cancelled Qty − Dispatch Qty · Buffer never enters this math."}>
        {fmtQtyInt(p)}
      </span>
    );
  } },
  // Mar 1, 2026 · Rate + Value columns — off by default; users flip on
  // via the "Columns" chooser. Rate is drawn from the line, value is
  // computed from total_qty × rate so the number stays consistent with
  // the SO editor's own totals bar.
  rate: { label: "Rate (₹)", align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const rate = Number(line.rate) || 0;
    if (!rate) return <span className="text-zinc-500">—</span>;
    return (
      <span className="font-mono text-zinc-300 tabular-nums text-xs" data-testid={`so-rate-${o?.order_no}-${lineIdx}`} title="Unit rate">
        {rate.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    );
  } },
  value: { label: "Value (₹)", align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const qty = Number(line.order_qty) || 0;
    const cancelled = Number(line.cancelled_qty) || 0;
    const rate = Number(line.rate) || 0;
    // Sep 04, 2026 · Value = (Order − Cancelled) × Rate. Buffer never
    // enters value math — parity with `_so_line_totals`.
    const value = Math.max(0, qty - cancelled) * rate;
    if (!value) return <span className="text-zinc-500">—</span>;
    return (
      <span className="font-mono text-emerald-300 tabular-nums text-xs font-semibold" data-testid={`so-value-${o?.order_no}-${lineIdx}`} title="Value = (Order − Cancelled) × Rate">
        ₹{value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    );
  } },
  // Legacy aliases kept for print/export code paths that still refer to them.
  articles:    { label: "Product Code",     align: "left",  render: (ctx) => COLS.article_no.render(ctx) },
  total_qty:   { label: "Total Qty",        align: "right", render: ({ line, lineIdx, o }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const qty = Number(line.order_qty) || 0;
    const extra = Number(line.extra_qty) || 0;
    return (
      <span className="font-mono text-zinc-400 tabular-nums" data-testid={`article-total-qty-alias-${o?.order_no}-${lineIdx}`} title="Total Qty = Order + Buffer · technical cap only">
        {fmtQtyInt(qty + extra)}
      </span>
    );
  } },
  selling_price: { label: "Rate",           align: "right", render: ({ line }) => line ? <span className="font-mono text-zinc-300">{fmtInr(line.rate)}</span> : <span className="text-zinc-500">—</span> },
  value:       { label: "Value",            align: "right", render: ({ line }) => {
    if (!line) return <span className="text-zinc-500">—</span>;
    const total = (Number(line.order_qty) || 0) + (Number(line.extra_qty) || 0);
    return <span className="font-mono">{fmtInr(total * (Number(line.rate) || 0))}</span>;
  } },
  delivery:      { label: "Delivery Date",  align: "left",  render: ({ o, lineDelivery }) => {
    const today = new Date().toISOString().slice(0, 10);
    const closed = (o.status || "").toLowerCase() === "closed";
    const overdue = !closed && lineDelivery && lineDelivery < today;
    if (!lineDelivery) return <span className="text-zinc-500">—</span>;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`font-mono text-xs ${overdue ? "text-amber-400 font-semibold" : "text-zinc-400"}`}>{lineDelivery}</span>
        {overdue && (
          <span data-testid={`overdue-${o.order_no}`} title="Delivery date has passed and order is not closed" className="px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 text-[9px] font-mono uppercase tracking-widest">Overdue</span>
        )}
      </span>
    );
  } },
  priority:      { label: "Priority",       align: "left",  render: ({ o, isFirst }) => isFirst ? <StatusBadge status={o.priority} /> : null },
  status:        { label: "Status",         align: "left",  render: ({ o, isFirst, downstreamLock }) => {
    if (!isFirst) return null;
    const authorized = o.authorized === true || ["Approved", "Authorized"].includes(o.status);
    // P1-SO-MANAGEMENT-AUTH-CONTROL · Phase 2B · UAT-01 Issue 2 · Feb 2026
    // Presentation-only fix — canonical Management Authorisation state
    // now derives from `approval_status`. Backend persistence (verified
    // during discovery) is correct: Reject writes `approval_status="Rejected"`;
    // Submit writes `approval_status="Pending Approval"`; Save (fresh)
    // writes `approval_status="Not Submitted"`. This mapping ensures the
    // user-facing badge distinguishes: Not Submitted / Waiting for Auth /
    // Rejected / Authorised.
    const appr = (o.approval_status || "").trim();
    const isPending = appr === "Pending Approval";
    const isRejected = appr === "Rejected";
    // P1-SO Phase 2B · UAT-02 · Feb 2026 — amendment-Draft state.
    const isReauth = appr === "Draft" && (o.approval_history || []).some(
      (e) => e?.action === "Draft" && e?.prev_status === "Authorized",
    );
    const isNotSubmitted = !authorized && !isPending && !isRejected && !isReauth && !["Closed", "Cancelled"].includes(o.status);
    return (
      <div className="flex items-center gap-1.5">
        <StatusBadge status={o.status} />
        {isPending && (
          <span
            data-testid={`so-waiting-auth-${o.order_no}`}
            title="Waiting for Management Authorisation — hidden from Packing, GRN and Production pickers until Authorised"
            className="px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[9px] font-mono uppercase tracking-widest"
          >
            Pending Authorisation
          </span>
        )}
        {isRejected && (
          <span
            data-testid={`so-rejected-${o.order_no}`}
            title={`Rejected by Management${o.rejection_comments ? ` — ${o.rejection_comments}` : ""}. Correct + Resubmit to re-enter the queue.`}
            className="px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-400 border border-red-500/30 text-[9px] font-mono uppercase tracking-widest"
          >
            Rejected
          </span>
        )}
        {isReauth && (
          <span
            data-testid={`so-reauth-${o.order_no}`}
            title="Amendment saved — awaiting Management Re-authorisation. Use Submit for Authorisation to enter the Management Authorisation Queue."
            className="px-1.5 py-0.5 rounded-sm bg-purple-500/15 text-purple-300 border border-purple-500/30 text-[9px] font-mono uppercase tracking-widest"
          >
            Awaiting Re-authorisation
          </span>
        )}
        {isNotSubmitted && (
          <span
            data-testid={`so-not-submitted-${o.order_no}`}
            title="Draft — Save is complete; use Submit for Authorisation to enter the Management Authorisation Queue."
            className="px-1.5 py-0.5 rounded-sm bg-zinc-500/15 text-zinc-300 border border-zinc-500/30 text-[9px] font-mono uppercase tracking-widest"
          >
            Draft
          </span>
        )}
        {downstreamLock && (
          <span data-testid={`downstream-lock-${o.order_no}`} title={`Linked to ${downstreamLock} · Edit / Delete disabled · Amend allowed fields only`} className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wide text-zinc-500">
            <LinkIcon size={9} weight="bold" />Linked
          </span>
        )}
      </div>
    );
  } },
  wo_status:     { label: "Linked WOs",     align: "left",  render: ({ o, isFirst, woSummary }) => {
    if (!isFirst) return null;
    const b = (woSummary || {})[`SO|${o.id}`] || (woSummary || {})[`ESO|${o.id}`];
    if (!b || !b.count) return <span className="text-[11px] text-zinc-600 font-mono">No WO</span>;
    const st = b.statuses || {};
    const parts = Object.entries(st).map(([k, v]) => `${v} ${k}`).join(" · ");
    // WO count = subtle information accent; status summary is secondary/muted.
    return (
      <div className="leading-tight" data-testid={`so-orders-wo-chip-${o.order_no}`} title={b.wos.map(w => `${w.wo_no} · ${w.item_code} · ${w.status}`).join("\n")}>
        <span className="font-mono text-xs font-semibold text-sky-300">{b.count} {b.count === 1 ? "WO" : "WOs"}</span>
        <span className="block text-[10px] font-mono text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px]">{parts}</span>
      </div>
    );
  } },
  actions:     { label: "Actions",    align: "right", width: "w-24 min-w-[96px]", render: (ctx) => {
    const { o, isFirst, line, lineIdx, authorize, submitForApproval, canAuthorize, canEdit, openEdit, remove, cancelOrder, setHistoryOrder, setPreview, printSo, openBulkForLine, canBulk, canEditRow, canCancelRow, canPrintRow, downstreamLock, setModifyTarget, setModifyOpen, setAdminAmend, setHoldAction } = ctx;
    const ctxPass = { setModifyTarget, setModifyOpen };
    // Feb 2026 · Strict Cancellation Guard · once an SO is cancelled it
    // becomes permanently read-only. Only View + Print stay on the row;
    // Authorize / Amend / Edit / Modify / Cancel are all suppressed so no
    // stale tab can revive a dead order.
    const isCancelled = (o.status || "").toLowerCase() === "cancelled";
    // P1-SO-MANAGEMENT-AUTH-CONTROL · Phase 2B · Feb 2026 — MA-INV-04.
    // Canonical Pending marker exposed on the SO row. Every mutation
    // action (Edit, Cancel/Delete, Modify, Authorize direct-jump) is
    // hidden while Pending. Only View, Print, History, and Reject-from-
    // Management-queue remain (Reject lives on the Management Approval
    // Queue page, not this grid — so nothing needs to be shown here).
    const isPending = (o.approval_status || "").trim() === "Pending Approval";
    const isRejected = (o.approval_status || "").trim() === "Rejected";
    // P1-SO Phase 2B · UAT-02 · Feb 2026 — amendment-Draft state
    // (Authorised SO that was Unapproved via Modify popover). Positive
    // provenance from approval_history; must be re-submittable.
    const isReauth = (o.approval_status || "").trim() === "Draft" && (o.approval_history || []).some(
      (e) => e?.action === "Draft" && e?.prev_status === "Authorized",
    );
    const isNotSubmitted = !o.authorized && !isPending && !isRejected && !isReauth && !isCancelled;
    // SO Register V1 · Correction 03 Rev-B · consolidated Actions dropdown
    // (Weaving Consumption pattern). Only existing permitted actions; all
    // handlers / RBAC / gates / downstream locks reused unchanged.
    const items = [];
    if (line && canBulk) items.push(
      <DropdownMenuItem key="bulk" data-testid={`bulk-slips-${o.order_no}-${lineIdx}`} onClick={() => openBulkForLine && openBulkForLine(o, line)}>Bulk Packing Slips…</DropdownMenuItem>
    );
    if (isFirst) {
      if ((isNotSubmitted || isRejected || isReauth) && canEditRow && canSubmitDoc("SO"))  // IMP-1C · D-1b · aligned with View Submit
        items.push(<DropdownMenuItem key="submit" data-testid={`submit-for-approval-${o.order_no}`} className="text-sky-300" onClick={() => submitForApproval(o)}>{isReauth ? "Submit for Re-authorisation" : isRejected ? "Resubmit for Authorisation" : "Submit for Authorisation"}</DropdownMenuItem>);
      if (isPending && canAuthorize && !isCancelled && (o.status || "").toLowerCase() !== "short closed")  // IMP-1C · D-1 / N-1
        items.push(<DropdownMenuItem key="auth" data-testid={`authorize-${o.order_no}`} className="text-emerald-300" onClick={() => authorize(o)}>Authorise</DropdownMenuItem>);
      items.push(<DropdownMenuItem key="view" data-testid={`view-${o.order_no}`} onClick={() => setPreview(o)}>View</DropdownMenuItem>);
      if (canPrintRow) items.push(<DropdownMenuItem key="print" data-testid={`print-${o.order_no}`} onClick={() => printSo(o)}>Print / PDF</DropdownMenuItem>);
      if (canPrintRow) items.push(<DropdownMenuItem key="dl-pdf" data-testid={`so-download-pdf-${o.order_no}`} onClick={() => downloadDoc("SO", o.id, "pdf")}>Download PDF</DropdownMenuItem>);
      if (canPrintRow) items.push(<DropdownMenuItem key="dl-xlsx" data-testid={`so-export-excel-${o.order_no}`} onClick={() => downloadDoc("SO", o.id, "xlsx")}>Export to Excel</DropdownMenuItem>);
      if ((o.authorized || !downstreamLock) && !isCancelled && !isPending && canEditRow)
        items.push(<DropdownMenuItem key="edit" data-testid={`edit-${o.order_no}`} onClick={() => openEdit(o)}>{o.authorized ? "Amend" : "Edit"}</DropdownMenuItem>);
      if (o.authorized && !isCancelled && !isPending && canEditRow)
        items.push(<DropdownMenuItem key="admin" data-testid={`admin-amend-${o.order_no}`} onClick={() => setAdminAmend({ id: o.id })}>Administrative Amendment…</DropdownMenuItem>);
      if ((o.amendments?.length || 0) > 0)
        items.push(<DropdownMenuItem key="hist" data-testid={`history-${o.order_no}`} onClick={() => setHistoryOrder(o)}>History ({o.amendments.length})</DropdownMenuItem>);
    }
    if (items.length === 0) return <span className="text-zinc-600">—</span>;
    const trigTid = isFirst ? `so-actions-${o.order_no}` : `so-actions-${o.order_no}-${lineIdx}`;
    return (
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={trigTid} className="h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap">Actions ▾</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm min-w-[180px]">
            {items}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }},
};


export function ArticleCombobox({ testid, articles, value, onChange, onCreated, styleFilter = "" }) {
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyArt = { article_type: "RNW", width: 0, article_no: "", colour_code: "", description: "", colour: "", size: "", grade: "I" };
  const [draft, setDraft] = useState(emptyArt);
  // Feb 2026 · bidirectional style ↔ article filter. When the SO line's
  // Style No has been typed, narrow the dropdown to only articles whose
  // `style_no` contains that substring (case-insensitive). Empty style →
  // show every article. Keeps the current selection visible even if its
  // style doesn't match (so amend-mode never hides the picked row).
  const styleQ = String(styleFilter || "").trim().toLowerCase();
  const visibleArticles = styleQ
    ? articles.filter(a => (a.style_no || "").toLowerCase().includes(styleQ) || a.id === value)
    : articles;

  const openAdd = (initialDesc = "") => {
    setDraft({ ...emptyArt, description: initialDesc });
    setShowAdd(true);
  };

  const saveNew = async () => {
    if (!draft.article_no.trim()) return toast.error("Article No is required");
    if (!draft.description.trim()) return toast.error("Description is required");
    setCreating(true);
    try {
      const { data } = await api.post("/articles/", draft);
      toast.success(`Added ${data.product_code || data.article_no}`);
      onCreated(data);
      setShowAdd(false);
      setDraft(emptyArt);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add article");
    } finally {
      setCreating(false);
    }
  };

  const inp2 = "h-9 w-full bg-[var(--bg-0)] border border-[var(--border)] px-3 rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
  const selCls2 = "h-9 bg-[var(--bg-0)] border-[var(--border)]";

  // Sep 04, 2026 · Refactored to lean on the universal SearchablePicker
  // for the popover + typeahead + keyboard-nav experience. The bespoke
  // Add-New-Article dialog + style-filter behaviour are preserved as a
  // footer slot + a filtered `options` array so operators still get
  // inline article creation without leaving the SO line.
  return (
    <>
      <SearchablePicker
        testid={testid || "article-picker"}
        value={value || ""}
        onChange={onChange}
        placeholder="Pick article…"
        emptyLabel={styleQ ? `No article carries style "${styleQ.toUpperCase()}"` : "No article found."}
        groupHeading={styleQ ? `Articles · ${visibleArticles.length}/${articles.length} match style` : "Articles"}
        headerHint={styleQ ? `Filtered by style "${styleQ.toUpperCase()}" · type to narrow further` : "Search by article no, description or style"}
        options={visibleArticles.map(a => ({
          value: a.id,
          code: a.product_code,
          name: a.description || "",
          meta: a.style_no || "",
          _row: a,
        }))}
        renderOption={(o) => (
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-mono text-sm break-all leading-tight">{o.code}</span>
            <div className="flex items-center gap-2 text-[11px] text-zinc-500 truncate">
              {o.meta && <span className="font-mono text-emerald-400/80">{o.meta}</span>}
              {o.name && <span className="truncate">{o.name}</span>}
            </div>
          </div>
        )}
        footer={({ query, close }) => (
          <button
            type="button"
            data-testid="article-add-new-btn"
            onClick={() => { openAdd(query); close(); }}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[var(--accent)] hover:bg-[var(--bg-2)] rounded-sm"
          >
            <PlusIcon size={14} weight="bold" />
            Add new article{query ? ` "${query}"` : ""}
          </button>
        )}
      />

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" data-testid="article-add-dialog" description="Register a new finished-good article inline — the composed Product Code will feed straight into the SO line.">
          <DialogHeader><DialogTitle className="font-display">New Article</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <F label="Type">
              <Select value={draft.article_type} onValueChange={v => setDraft({ ...draft, article_type: v })}>
                <SelectTrigger className={selCls2}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {["RNW", "RNJ", "Woven", "Knitted", "Jacquard", "Crochet"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Width (mm)"><FormattedNumberInput data-testid="art-width" value={draft.width} onChange={e => setDraft({ ...draft, width: parseFloat(e.target.value) || 0 })} className={inp2} /></F>
            <F label="Article No *"><input data-testid="art-no" value={draft.article_no} onChange={e => setDraft({ ...draft, article_no: e.target.value.toUpperCase() })} className={inp2} placeholder="e.g. A001" /></F>
            <F label="Colour Code"><input data-testid="art-cc" value={draft.colour_code} onChange={e => setDraft({ ...draft, colour_code: e.target.value.toUpperCase() })} className={inp2} placeholder="e.g. NAV" /></F>
            <F label="Colour"><input data-testid="art-colour" value={draft.colour} onChange={e => setDraft({ ...draft, colour: e.target.value })} className={inp2} placeholder="Navy" /></F>
            <F label="Size"><input data-testid="art-size" value={draft.size} onChange={e => setDraft({ ...draft, size: e.target.value })} className={inp2} placeholder="M / L / 73CM" /></F>
            <F label="Description *" className="col-span-2"><input data-testid="art-desc" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} className={inp2} placeholder="e.g. Sports waistband navy" /></F>
            <F label="Grade"><input data-testid="art-grade" value={draft.grade} onChange={e => setDraft({ ...draft, grade: e.target.value.toUpperCase() })} className={inp2} placeholder="I / II / III / SPECIAL" /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="art-save-btn" disabled={creating} onClick={saveNew} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white disabled:opacity-60">{creating ? "Saving…" : "Save Article"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RiskBadge({ risk, orderNo }) {
  const map = {
    critical: { cls: "bg-red-500/15 text-red-400 border-red-500/40", dot: "bg-red-400", pulse: true, prefix: "AT RISK" },
    at_risk:  { cls: "bg-amber-500/15 text-amber-400 border-amber-500/40", dot: "bg-amber-400", pulse: false, prefix: "WATCH" },
    on_track: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400", pulse: false, prefix: "ON TRACK" },
    delivered:{ cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30", dot: "bg-zinc-400", pulse: false, prefix: "DELIVERED" },
  };
  const cfg = map[risk?.state] || map.on_track;
  const label = risk?.label || cfg.prefix;
  return (
    <span
      data-testid={`risk-badge-${orderNo}-${risk?.state || "unknown"}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest w-fit ${cfg.cls}`}
      title={`${cfg.prefix} · ${Math.round(risk?.progress_pct || 0)}% complete`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? "animate-pulse" : ""}`} />
      {label}
    </span>
  );
}

function SoStatusReport() {
  const [data, setData] = useState({ rows: [], generated_at: "" });
  const [woSummary, setWoSummary] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [dcByNo, setDcByNo] = useState({});
  const [plByNo, setPlByNo] = useState({});
  const [previewDc, setPreviewDc] = useState(null);
  const [previewPl, setPreviewPl] = useState(null);
  const [presetKey, setPresetKey] = useState("");
  // Order Status V1 · local density toggle (does not touch the sealed SO
  // Register). Persisted under `mes.so_report.density`. Applied via a
  // scoped Tailwind descendant variant on the table wrapper only.
  const [soRepDensity, setSoRepDensity] = useState(() => {
    try { return localStorage.getItem("mes.so_report.density") || "comfortable"; } catch { return "comfortable"; }
  });
  const setSoRepDensityPersisted = (d) => {
    setSoRepDensity(d);
    try { localStorage.setItem("mes.so_report.density", d); } catch (e) { /* ignore */ }
  };
  // Feb 2026 · Column-visibility picker (mirrors ESO Status Report).
  // Order No, expand-caret and Actions are pinned; every other column is
  // toggleable. Persisted under `mes.so_report.cols` in localStorage.
  const ALL_COLS = [
    { k: "order_date", label: "Order Date" },
    { k: "customer",   label: "Customer" },
    { k: "po",         label: "PO No" },
    { k: "item",       label: "Item No" },
    { k: "style",      label: "Style No" },
    { k: "article",    label: "Product Code" },
    { k: "order_qty",  label: "Order Qty" },
    { k: "original_qty", label: "Original Qty" },
    { k: "add_qty",      label: "Add Qty" },
    { k: "buffer_qty", label: "Buffer Qty" },
    { k: "total_qty",  label: "Total Qty" },
    { k: "dispatched", label: "Dispatch Qty" },
    { k: "pending",    label: "Pending Qty" },
    { k: "hold_qty",         label: "Hold Qty" },
    { k: "short_closed_qty", label: "Short Closed Qty" },
    { k: "cancelled_qty",    label: "Cancelled Qty" },
    { k: "delivery",   label: "Delivery" },
    { k: "rate",       label: "Rate" },
    { k: "value",      label: "Value" },
    { k: "status",     label: "Status" },
    { k: "fulfilment", label: "Fulfilment" },
    { k: "wo_status",  label: "Linked WOs" },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.so_report.cols");
      if (raw) return new Set(JSON.parse(raw));
    } catch (e) { /* ignore */ }
    // R2-01L A4-02 · G7 · optional Original / Add columns are OFF by default.
    return new Set(ALL_COLS.map(c => c.k).filter(k => k !== "original_qty" && k !== "add_qty"));
  });
  const [showColsMenu, setShowColsMenu] = useState(false);
  const toggleCol = (k) => setVisibleCols(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.so_report.cols", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const showAllCols = () => {
    const next = new Set(ALL_COLS.map(c => c.k));
    setVisibleCols(next);
    try { localStorage.setItem("mes.so_report.cols", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
  };
  const col = (k) => visibleCols.has(k);
  const totalCols = 3 + Array.from(visibleCols).length; // expand + Order No + Actions + toggleable

  // Quick-range presets. Uses local time (avoids UTC-shift off-by-one for IST users).
  // Indian FY = Apr 1 → Mar 31.
  const applyPreset = (key) => {
    const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    let from = "", to = "";
    if (key === "today") { const t = fmt(now); from = t; to = t; }
    else if (key === "week") {
      const dow = (now.getDay() + 6) % 7; // 0 = Monday
      from = fmt(new Date(y, m, d - dow));
      to = fmt(now);
    } else if (key === "month") {
      from = fmt(new Date(y, m, 1));
      to = fmt(now);
    } else if (key === "fy") {
      const fyStart = m >= 3 ? y : y - 1; // April is month index 3
      from = fmt(new Date(fyStart, 3, 1));
      to = fmt(new Date(fyStart + 1, 2, 31));
    }
    setFromDate(from); setToDate(to); setPresetKey(key);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [rep, dcs, pls, wos] = await Promise.all([
        api.get("/reports/so-status").catch(() => ({ data: { rows: [] } })),
        api.get("/delivery-challans/").catch(() => ({ data: [] })),
        api.get("/packing-lists/").catch(() => ({ data: [] })),
        api.get("/work-orders/summary/by-source").catch(() => ({ data: {} })),
      ]);
      setData(rep.data);
      const dm = {}; (dcs.data || []).forEach(d => { if (d.dc_no) dm[d.dc_no] = d; });
      const pm = {}; (pls.data || []).forEach(p => { if (p.pl_no) pm[p.pl_no] = p; });
      setDcByNo(dm); setPlByNo(pm);
      setWoSummary(wos.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Apply date range: keep an order if its order_date is within range OR at least one of its dispatches is.
  // The dispatches inside the order are filtered to the range for display and totals.
  const inRange = (d) => (!fromDate || (d || "") >= fromDate) && (!toDate || (d || "") <= toDate);
  const filtered = data.rows
    .map(r => {
      const disp = (fromDate || toDate) ? r.dispatches.filter(d => inRange(d.date)) : r.dispatches;
      const disp_qty = (fromDate || toDate) ? disp.reduce((a, d) => a + d.quantity, 0) : r.disp_qty;
      // Sep 03, 2026 · Pending Qty parity fix. Previously we recomputed
      // `pending_qty = max(0, r.total_qty - disp_qty)` here, which
      // ignored `cancelled_qty` entirely and used an ORDER-level floor
      // instead of a LINE-level one — producing a phantom-inflated
      // pending on live data (5,01,330 vs the canonical 4,81,135 from
      // /reports/pending-statement). The backend already exposes the
      // correct per-line-floored `pending_qty` on every SO row, so we
      // trust that unchanged. When a date filter is active, we still
      // scope disp_qty to the range for the informational total, but
      // pending_qty is scoped by proportional line-fold rather than
      // ORDER subtraction to keep the "over-dispatched line offset"
      // bug from returning.
      let pending_qty = r.pending_qty;
      if ((fromDate || toDate) && (r.lines || []).length) {
        // R2-01L A4-02 · G8 · as-of rule: pending at the range end = Order − Cancelled − dispatch up to `toDate`
        // (buffer excluded · short-closed / cancelled-order lines stay 0). Line share approximates per-line dispatch.
        const dispAsOf = (r.dispatches || []).filter(d => !toDate || (d.date || "") <= toDate).reduce((a, d) => a + d.quantity, 0);
        const soCancelled = isOrderCancelled(r);
        pending_qty = (r.lines || []).reduce((acc, li) => {
          if (soCancelled || li.short_closed) return acc;
          const authQ = Math.max(0, (Number(li.order_qty) || 0) - (Number(li.cancelled_qty) || 0));
          const lineShare = (Number(li.disp_qty) || 0) > 0 && r.disp_qty > 0
            ? (Number(li.disp_qty) / r.disp_qty) * dispAsOf
            : 0;
          return acc + Math.max(0, authQ - lineShare);
        }, 0);
      }
      return { ...r, dispatches: disp, disp_qty, pending_qty };
    })
    .filter(r => {
      const stq = (r.status || "").toLowerCase();
      // Phase 2C-5B · "Open" = actionable/active only — terminal states
      // (Closed / Completed / Cancelled / Short Closed / Rejected) drop out.
      const TERMINAL = ["closed", "completed", "cancelled", "canceled", "short closed", "rejected"];
      if (statusFilter === "open" && TERMINAL.includes(stq)) return false;
      if (statusFilter === "closed" && stq !== "closed") return false;
      if (search) {
        const q = search.toLowerCase();
        const articles = (r.articles || []).join(" ").toLowerCase();
        const match = (r.order_no || "").toLowerCase().includes(q) || (r.customer_name || "").toLowerCase().includes(q) || (r.po_number || "").toLowerCase().includes(q) || articles.includes(q);
        if (!match) return false;
      }
      // when a date range is active, only keep orders that either fall in the order-date range
      // OR have at least one dispatch in the range
      if (fromDate || toDate) {
        const orderInRange = inRange(r.order_date);
        if (!orderInRange && r.dispatches.length === 0) return false;
      }
      return true;
    });
  // Mar 3, 2026 · Now roll up ₹ values alongside qty. Uses per-line rate so
  // Dispatched Value / Pending Value stay accurate under partial dispatch.
  const totals = filtered.reduce((a, r) => {
    a.order  += Number(r.order_qty || 0);
    a.buffer += Number(r.extra_qty || r.buffer_qty || 0);
    a.total += r.total_qty;
    a.disp  += r.disp_qty;
    a.pending += r.pending_qty;
    a.hold += Number(r.hold_qty || 0);
    a.shortClosed += Number(r.short_closed_qty || 0);
    a.cancelled += Number(r.cancelled_qty_net || 0);
    (r.lines || []).forEach(li => {
      const rate = Number(li.rate) || 0;
      a.totalVal   += (Number(li.value) || ((Number(li.total_qty)   || 0) * rate));
      a.dispVal    += ((Number(li.disp_qty)    || 0) * rate);
      a.pendingVal += ((Number(li.pending_qty) || 0) * rate);
    });
    return a;
  }, { order: 0, buffer: 0, total: 0, disp: 0, pending: 0, hold: 0, shortClosed: 0, cancelled: 0, totalVal: 0, dispVal: 0, pendingVal: 0 });

  const dateRangeLabel = (fromDate || toDate) ? `${fromDate || "…"} → ${toDate || "…"}` : "All dates";

  const exportCsv = () => {
    const flat = [];
    filtered.forEach(r => {
      if (r.dispatches.length === 0) {
        flat.push({ ...r, dispatch_date: "", dc_no: "", pl_nos: "", dispatch_qty: 0, vehicle_no: "" });
      } else {
        r.dispatches.forEach(d => flat.push({ ...r, dispatch_date: d.date, dc_no: d.dc_no, pl_nos: (d.pl_nos || []).join(", "), dispatch_qty: d.quantity, vehicle_no: d.vehicle_no }));
      }
    });
    exportToCSV("so-status-report.csv", flat, [
      { key: "order_no", label: "Order No" }, { key: "order_date", label: "Order Date" }, { key: "delivery_date", label: "Delivery Date" },
      { key: "customer_name", label: "Customer" }, { key: "po_number", label: "PO No" }, { key: "status", label: "Status" },
      { key: "order_qty", label: "Order Qty" }, { key: "extra_qty", label: "Buffer Qty" }, { key: "total_qty", label: "Total Qty" },
      { key: "disp_qty", label: "Dispatch Qty" }, { key: "pending_qty", label: "Pending Qty" },
      { key: "hold_qty", label: "Hold Qty" }, { key: "short_closed_qty", label: "Short Closed Qty" }, { key: "cancelled_qty_net", label: "Cancelled Qty" },
      { key: "dispatch_date", label: "Dispatch Date" }, { key: "dc_no", label: "DC No" }, { key: "pl_nos", label: "Packing List No(s)" }, { key: "dispatch_qty", label: "Dispatch Qty" }, { key: "vehicle_no", label: "Vehicle" },
    ]);
  };

  const printReport = () => {
    printDocument({
      kind: "Sales Order Status Report",
      no: fmtDateTimeIST(data.generated_at || new Date().toISOString()),
      meta: [
        { label: "Date Range", value: dateRangeLabel },
        { label: "Filter", value: statusFilter.toUpperCase() },
        { label: "Total Orders", value: filtered.length },
        { label: "Order Qty", value: fmtQtyInt(totals.order) },
        { label: "Dispatched", value: fmtQtyInt(totals.disp) },
        { label: "Pending", value: fmtQtyInt(totals.pending) },
      ],
      sections: [{
        heading: `Order Summary · ${filtered.length}`,
        columns: [
          { key: "order_no", label: "Order No", mono: true },
          { key: "order_date", label: "Order Date", mono: true },
          { key: "customer_name", label: "Customer" },
          { key: "po_number", label: "PO No", mono: true },
          { key: "status", label: "Status" },
          { key: "order_qty", label: "Order Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "extra_qty", label: "Buffer Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "total_qty", label: "Total Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "disp_qty", label: "Dispatch Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "pending_qty", label: "Pending Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "hold_qty", label: "Hold Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "short_closed_qty", label: "Short Closed Qty", align: "right", fmt: v => fmtQtyInt(v) },
          { key: "cancelled_qty_net", label: "Cancelled Qty", align: "right", fmt: v => fmtQtyInt(v) },
        ],
        rows: filtered,
        footerRow: { order_no: `Total ${filtered.length}`, order_qty: fmtQtyInt(totals.order), extra_qty: fmtQtyInt(totals.buffer), total_qty: fmtQtyInt(totals.total), disp_qty: fmtQtyInt(totals.disp), pending_qty: fmtQtyInt(totals.pending), hold_qty: fmtQtyInt(totals.hold), short_closed_qty: fmtQtyInt(totals.shortClosed), cancelled_qty_net: fmtQtyInt(totals.cancelled) },
      }],
      signatures: ["Prepared By", "Reviewed By", "Approved"],
    });
  };

  const printOne = (r) => {
    printDocument({
      kind: "Sales Order Status",
      no: r.order_no,
      meta: [
        { label: "Customer", value: r.customer_name || "—" },
        { label: "PO No", value: r.po_number || "—" },
        { label: "Order Date", value: r.order_date },
        { label: "Delivery Date", value: r.delivery_date },
        { label: "Status", value: (r.status || "").toUpperCase() + (r.authorized ? " · AUTHORIZED" : "") },
        { label: "Date Range", value: dateRangeLabel },
        { label: "Order Qty", value: fmtQtyInt(r.order_qty) },
        { label: "Dispatched", value: fmtQtyInt(r.disp_qty) },
        { label: "Pending", value: fmtQtyInt(r.pending_qty) },
      ],
      sections: r.dispatches.length > 0 ? [{
        heading: `Date-wise Dispatch Detail · ${r.dispatches.length}`,
        columns: [
          { key: "date", label: "Date", mono: true },
          { key: "dc_no", label: "DC No", mono: true },
          { key: "pl_nos_str", label: "PL No(s)", mono: true },
          { key: "vehicle_no", label: "Vehicle", mono: true },
          { key: "quantity", label: "Qty (m)", align: "right", fmt: v => fmtQtyInt(v) },
        ],
        rows: r.dispatches.map(d => ({ ...d, pl_nos_str: (d.pl_nos || []).join(", ") || "—" })),
        footerRow: { date: `Total ${r.dispatches.length} dispatches`, quantity: fmtQtyInt(r.dispatches.reduce((a, d) => a + d.quantity, 0)) },
      }] : [{
        heading: "No dispatches in the selected date range",
        columns: [{ key: "note", label: "Note" }],
        rows: [{ note: "This order has no delivered challans in the current filter window." }],
      }],
      signatures: ["Prepared By", "Reviewed By", "Approved"],
    });
  };

  return (
    <div className="space-y-4">
      {/* Mar 3, 2026 · Slim pinned summary bar — matches the Orders tab
          pattern (SalesOrders.jsx ~L694). Compact single-line strip with
          count + qty totals + ₹ value totals inline, so leadership scans
          fulfilment AND revenue without eating vertical space. Reflects
          the currently active filter (status / date / search). */}
      {(() => {
        const cell = "font-mono tabular-nums";
        const shipPct = totals.order > 0 ? Math.round((totals.disp / totals.order) * 100) : 0;
        return (
          <div
            data-testid="so-report-totals-strip"
            className="flex flex-wrap items-center gap-x-5 gap-y-1 px-3 py-1.5 border border-[var(--border)] bg-[var(--bg-1)] rounded-sm text-xs"
          >
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Orders</span>
            <span className={cell + " text-white font-semibold"} data-testid="so-report-total-orders">{filtered.length}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Order Qty (m)</span>
            <span className={cell + " text-[var(--accent)] font-semibold"} data-testid="so-report-total-qty">{fmtQtyInt(totals.order)}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Dispatched</span>
            <span className={cell + " text-emerald-300 font-semibold"} data-testid="so-report-total-dispatched">{fmtQtyInt(totals.disp)}</span>
            {totals.order > 0 && (
              <span className="text-[9px] font-mono text-zinc-500 tabular-nums">({shipPct}%)</span>
            )}

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending</span>
            <span className={cell + ` font-semibold ${totals.pending > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="so-report-total-pending">{fmtQtyInt(totals.pending)}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Total Value</span>
            <span className={cell + " text-white font-semibold"} data-testid="so-report-total-value">{fmtInr(totals.totalVal, {symbol: true})}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Dispatched Value</span>
            <span className={cell + " text-emerald-300 font-semibold"} data-testid="so-report-dispatched-value">{fmtInr(totals.dispVal, {symbol: true})}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending Value</span>
            <span className={cell + ` font-semibold ${totals.pendingVal > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="so-report-pending-value">{fmtInr(totals.pendingVal, {symbol: true})}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">{dateRangeLabel} · {statusFilter}</span>
          </div>
        );
      })()}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 mr-auto flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Filter</div>
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
            {[
              { k: "all", label: "All", count: data.rows.length },
              { k: "open", label: "Open", count: data.rows.filter(r => !["closed","completed","cancelled","canceled","short closed","rejected"].includes((r.status || "").toLowerCase())).length },
              { k: "closed", label: "Closed", count: data.rows.filter(r => (r.status || "").toLowerCase() === "closed").length },
            ].map(t => (
              <button key={t.k} data-testid={`report-filter-${t.k}`} onClick={() => setStatusFilter(t.k)} className={`px-3 h-8 text-xs font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${statusFilter === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}>
                {t.label}<span className={`ml-1.5 font-mono text-[10px] ${statusFilter === t.k ? "opacity-80" : "text-zinc-500"}`}>{t.count}</span>
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">Date</div>
          <input data-testid="report-from" type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPresetKey(""); }} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          <span className="text-zinc-500 text-xs">→</span>
          <input data-testid="report-to" type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPresetKey(""); }} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden ml-1">
            {[
              { k: "today",  label: "Today" },
              { k: "week",   label: "This Week" },
              { k: "month",  label: "This Month" },
              { k: "fy",     label: "This FY" },
            ].map(p => {
              const active = presetKey === p.k;
              return (
                <button
                  key={p.k}
                  data-testid={`report-preset-${p.k}`}
                  onClick={() => applyPreset(p.k)}
                  title={p.k === "fy" ? "Indian Financial Year · Apr–Mar" : ""}
                  className={`px-2.5 h-8 text-[11px] font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${active ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          {(fromDate || toDate) && (
            <button data-testid="report-clear-date" onClick={() => { setFromDate(""); setToDate(""); setPresetKey(""); }} className="h-8 px-2 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
          )}
          <input data-testid="report-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Order / Customer / PO / Article No…" className="h-8 w-64 bg-[var(--bg-0)] border border-[var(--border)] px-3 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)] ml-2" />
        </div>
        <button onClick={printReport} data-testid="report-print-btn" className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold rounded-sm flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print / PDF</button>
        <button onClick={exportCsv} data-testid="report-csv-btn" className="h-9 px-4 border border-[var(--border)] text-sm font-semibold rounded-sm flex items-center gap-2"><DownloadSimpleIcon size={14} weight="bold" />Export CSV</button>
        {/* Order Status V1 · local density toggle */}
        <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" role="group" aria-label="Row density">
          {[["comfortable", "Comfy"], ["compact", "Compact"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`report-density-${k}`}
              onClick={() => setSoRepDensityPersisted(k)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 ${soRepDensity === k ? "bg-[var(--accent)]/15 text-[var(--accent)]" : "text-zinc-400 hover:text-zinc-200"}`}
            >{l}</button>
          ))}
        </div>
        {/* Feb 2026 · Column-visibility picker */}
        <div className="relative">
          <button
            data-testid="so-report-cols-btn"
            onClick={() => setShowColsMenu(v => !v)}
            className="h-9 px-3 border border-[var(--border)] text-xs text-zinc-300 hover:bg-[var(--bg-2)] rounded-sm flex items-center gap-2"
            title="Show/hide columns"
          ><span className="font-mono">{visibleCols.size}/{ALL_COLS.length}</span>Columns</button>
          {showColsMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowColsMenu(false)} />
              <div data-testid="so-report-cols-menu" className="absolute right-0 top-full mt-1 z-50 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl w-52 py-1">
                <div className="px-3 py-1.5 border-b border-[var(--border)] flex items-center justify-between">
                  <span className="overline">Toggle Columns</span>
                  <button onClick={showAllCols} data-testid="so-report-cols-all" className="text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline">Show All</button>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {ALL_COLS.map(c => (
                    <label key={c.k} data-testid={`so-report-col-toggle-${c.k}`} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[var(--bg-2)]">
                      <input type="checkbox" checked={visibleCols.has(c.k)} onChange={() => toggleCol(c.k)} className="w-3.5 h-3.5 accent-[var(--accent)]" />
                      <span className="text-zinc-300">{c.label}</span>
                    </label>
                  ))}
                </div>
                <div className="px-3 py-1.5 border-t border-[var(--border)] text-[10px] text-zinc-500">Preference saved per user (localStorage).</div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={`border border-[var(--border)] rounded-sm overflow-x-auto ${soRepDensity === "compact" ? "[&_td]:!py-1.5 [&_td]:!px-2 [&_th]:!py-1.5 [&_th]:!px-2" : "[&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5"}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)] sticky top-0 z-20 bg-[var(--bg-1)]">
              <th className="text-left !px-1 w-7"></th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Order No</th>
              {col("order_date") && <th className="text-left px-4 py-3">Order Date</th>}
              {col("customer")   && <th className="text-left px-4 py-3">Customer</th>}
              {col("po")         && <th className="text-left px-4 py-3">PO No</th>}
              {col("item")       && <th className="text-right px-4 py-3">Item No</th>}
              {col("style")      && <th className="text-left px-4 py-3">Style No</th>}
              {col("article")    && <th className="text-left px-4 py-3 sticky left-0 z-30 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]">Product Code</th>}
              {col("order_qty")  && <th className="text-right px-4 py-3">Order Qty</th>}
              {col("original_qty") && <th className="text-right px-4 py-3" title="Original ordered quantity · read-only">Original Qty</th>}
              {col("add_qty")      && <th className="text-right px-4 py-3">Add Qty</th>}
              {col("buffer_qty") && <th className="text-right px-4 py-3">Buffer Qty</th>}
              {col("total_qty")  && <th className="text-right px-4 py-3">Total Qty</th>}
              {col("dispatched") && <th className="text-right px-4 py-3">Dispatch Qty</th>}
              {col("pending")    && <th className="text-right px-4 py-3" title="Pending = Order Qty − Cancelled Qty − Dispatch Qty">Pending Qty</th>}
              {col("hold_qty")         && <th className="text-right px-4 py-3" title="Outstanding balance while the order is on Hold">Hold Qty</th>}
              {col("short_closed_qty") && <th className="text-right px-4 py-3" title="Quantity commercially short-closed">Short Closed Qty</th>}
              {col("cancelled_qty")    && <th className="text-right px-4 py-3" title="Quantity extinguished by cancellation (net of short-close)">Cancelled Qty</th>}
              {col("delivery")   && <th className="text-left px-4 py-3">Delivery Date</th>}
              {col("rate")       && <th className="text-right px-4 py-3">Rate</th>}
              {col("value")      && <th className="text-right px-4 py-3">Value</th>}
              {col("status")     && <th className="text-left px-4 py-3">Status</th>}
              {col("fulfilment") && <th className="text-left px-4 py-3" title="Derived from Delivered dispatch vs commercial Order Qty (buffer excluded). Independent of lifecycle status.">Fulfilment</th>}
              {col("wo_status")  && <th className="text-left px-4 py-3">Linked WOs</th>}
              <th className="text-right px-4 py-3 w-24 min-w-[96px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={totalCols} className="text-center py-14 text-zinc-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={totalCols} className="text-center py-14 text-zinc-500">No orders match this filter.</td></tr>}
            {!loading && filtered.flatMap(r => {
              const isOpen = expanded[r.order_id];
              const lines = (r.lines && r.lines.length) ? r.lines : [null];
              const out = [];
              lines.forEach((line, lineIdx) => {
                const isFirst = lineIdx === 0;
                const isLast = lineIdx === lines.length - 1;
                const lineDelivery = line?.delivery_date || r.delivery_date;
                const total = line ? line.total_qty : 0;
                out.push(
                  <tr
                    key={`${r.order_id}-${lineIdx}`}
                    className={`${!isLast ? "border-b border-[var(--border)]/40" : "border-b border-[var(--border)]"} align-top hover:bg-[var(--bg-2)]/40 ${(r.status || "").toLowerCase() === "cancelled" ? "[&>td]:text-zinc-500 opacity-75" : ""}`}
                    data-testid={`report-row-${r.order_no}-${lineIdx}`}
                  >
                    <td className="!px-1 py-3 text-center w-7">
                      {isFirst && r.dispatches.length > 0 && (
                        <button onClick={() => setExpanded({ ...expanded, [r.order_id]: !isOpen })} data-testid={`report-expand-${r.order_no}`} className="text-[var(--accent)] font-mono text-xs">{isOpen ? "▼" : "▶"}</button>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-left">
                      {/* Order Status · full SO No on ONE line, repeated on every
                          line-item row. Shared TwoLineDocNo untouched. */}
                      <span
                        data-testid={isFirst ? `report-order-no-${r.order_no}` : `report-order-no-${r.order_no}-${lineIdx}`}
                        className="font-mono whitespace-nowrap text-white text-xs font-semibold"
                      >{r.order_no}</span>
                    </td>
                    {col("order_date") && <td className="px-4 py-3 font-mono text-zinc-400 whitespace-nowrap">{r.order_date || ""}</td>}
                    {col("customer")   && <td className="px-4 py-3 min-w-[150px] max-w-[190px]">
                      <span className="text-zinc-200 font-medium block leading-tight line-clamp-2" title={r.customer_name || "—"}>{r.customer_name || "—"}</span>
                    </td>}
                    {col("po")         && <td className="px-4 py-3 font-mono text-zinc-400 whitespace-nowrap">{r.po_number || "—"}</td>}
                    {col("item")       && <td className="px-4 py-3 text-right font-mono text-zinc-300 text-xs tabular-nums">{line ? (line.item_no || String(lineIdx + 1).padStart(3, "0")) : <span className="text-zinc-500">—</span>}</td>}
                    {col("style")      && <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{line ? (line.style_no || "—") : <span className="text-zinc-500">—</span>}</td>}
                    {col("article") && <td className="px-4 py-3 sticky left-0 z-10 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]">
                      {line ? (
                        /* Mar 3, 2026 · Product Code cell — mirrors the Orders tab
                           COLS.article_no styling (accent, mono, whitespace-nowrap,
                           dynamic width so long codes push the column instead of
                           wrapping mid-string). Full technical string; base article
                           lives on the SO grid via baseArticle() elsewhere. */
                        <span
                          className="font-mono text-[var(--accent)] text-xs whitespace-nowrap"
                          data-testid={`report-line-${r.order_no}-${lineIdx}`}
                          title={line.article_no || ""}
                        >
                          {line.article_no || "—"}
                        </span>
                      ) : <span className="text-zinc-500">—</span>}
                    </td>}
                    {col("order_qty") && <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums" data-testid={`report-order-qty-${r.order_no}-${lineIdx}`}>
                      {line ? fmtQtyInt(Number(line.order_qty) || 0) : <span className="text-zinc-500">—</span>}
                    </td>}
                    {col("original_qty") && <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-300" data-testid={`report-original-qty-${r.order_no}-${lineIdx}`}>
                      {!line ? <span className="text-zinc-500">—</span> : line.original_qty === null || line.original_qty === undefined ? <span className="text-zinc-500 text-[10px]">Not established</span> : fmtQtyInt(line.original_qty)}
                    </td>}
                    {col("add_qty") && <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-300" data-testid={`report-add-qty-${r.order_no}-${lineIdx}`}>
                      {line ? fmtQtyInt(Number(line.add_qty) || 0) : <span className="text-zinc-500">—</span>}
                    </td>}
                    {col("buffer_qty") && <td className="px-4 py-3 text-right font-mono text-zinc-300 tabular-nums" data-testid={`report-buffer-qty-${r.order_no}-${lineIdx}`} title="Buffer Qty · technical over-dispatch cap. Never enters Pending math.">
                      {line ? fmtQtyInt(Number(line.extra_qty) || 0) : <span className="text-zinc-500">—</span>}
                    </td>}
                    {col("total_qty") && <td className="px-4 py-3 text-right font-mono text-zinc-400 tabular-nums" data-testid={`report-total-qty-${r.order_no}-${lineIdx}`} title="Total Qty = Order + Buffer · technical cap only">
                      {line ? fmtQtyInt(total) : <span className="text-zinc-500">—</span>}
                    </td>}
                    {col("dispatched") && <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-400">{line ? fmtQtyInt(line.disp_qty) : "0"}</td>}
                    {col("pending")    && <td className={`px-4 py-3 text-right font-mono tabular-nums ${line && line.pending_qty > 0 ? "text-amber-400" : "text-zinc-500"}`}>{line ? fmtQtyInt(line.pending_qty) : "0"}</td>}
                    {col("hold_qty")         && <td className={`px-4 py-3 text-right font-mono tabular-nums ${line && line.hold_qty > 0 ? "text-orange-300" : "text-zinc-600"}`} data-testid={`report-hold-qty-${r.order_no}-${lineIdx}`}>{line ? fmtQtyInt(line.hold_qty || 0) : "0"}</td>}
                    {col("short_closed_qty") && <td className={`px-4 py-3 text-right font-mono tabular-nums ${line && line.short_closed_qty > 0 ? "text-purple-300" : "text-zinc-600"}`} data-testid={`report-short-closed-qty-${r.order_no}-${lineIdx}`}>{line ? fmtQtyInt(line.short_closed_qty || 0) : "0"}</td>}
                    {col("cancelled_qty")    && <td className={`px-4 py-3 text-right font-mono tabular-nums ${line && line.cancelled_qty_net > 0 ? "text-rose-300" : "text-zinc-600"}`} data-testid={`report-cancelled-qty-${r.order_no}-${lineIdx}`}>{line ? fmtQtyInt(line.cancelled_qty_net || 0) : "0"}</td>}
                    {col("delivery") && <td className="px-4 py-3">
                      {lineDelivery ? (() => {
                        // Delivery mirrors the SEALED SO Register exactly: inline-flex,
                        // no nowrap — the column stays compact and the date wraps to a
                        // second line (with the Overdue pill) when space is tight.
                        const risk = r.delivery_risk || {};
                        const overdue = (risk.days_left != null && risk.days_left < 0) || /overdue/i.test(risk.label || "");
                        return (
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`font-mono text-xs ${overdue ? "text-amber-400 font-semibold" : "text-zinc-400"}`}>{lineDelivery}</span>
                            {overdue && <span data-testid={`report-overdue-${r.order_no}-${lineIdx}`} title={risk.label || "Delivery date has passed"} className="px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 text-[9px] font-mono uppercase tracking-widest">Overdue</span>}
                          </span>
                        );
                      })() : <span className="text-zinc-500">—</span>}
                    </td>}
                    {col("rate")  && <td className="px-4 py-3 text-right font-mono text-zinc-300">{line ? `₹${Number(line.rate || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                    {col("value") && <td className="px-4 py-3 text-right font-mono">{line ? `₹${Number(line.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                    {col("status") && <td className="px-4 py-3">
                      {isFirst && (() => {
                        // Lifecycle is the PRIMARY status. Delivery-risk now lives in
                        // the Delivery cell so it no longer competes here.
                        const s = r.status || "Open";
                        const sl = s.toLowerCase();
                        const tone = (sl === "closed" || sl === "completed" || sl === "running") ? "bg-emerald-500/10 text-emerald-400"
                          : (sl === "cancelled" || sl === "canceled" || sl === "rejected") ? "bg-rose-500/10 text-rose-400"
                          : sl === "hold" ? "bg-orange-500/10 text-orange-400"
                          : sl === "short closed" ? "bg-purple-500/10 text-purple-300"
                          : "bg-blue-500/10 text-blue-400";
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-widest w-fit ${tone}`} data-testid={`report-status-${r.order_no}`}>
                            {(sl === "closed" || sl === "completed") && <LockIcon size={10} weight="bold" />}{s}
                          </span>
                        );
                      })()}
                    </td>}
                    {col("fulfilment") && <td className="px-4 py-3">
                      {line && line.fulfilment_status ? (
                        <span
                          data-testid={`report-fulfilment-${r.order_no}-${lineIdx}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-widest w-fit ${
                            line.fulfilment_status === "Fully Dispatched" ? "bg-emerald-500/10 text-emerald-400"
                              : line.fulfilment_status === "Partially Dispatched" ? "bg-amber-500/10 text-amber-400"
                              : "bg-zinc-500/10 text-zinc-400"}`}
                        >{line.fulfilment_status}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>}
                    {col("wo_status")  && <td className="px-4 py-3">
                      {isFirst && (() => {
                        // SO-level WO summary (linkage confirmed SO-level in Phase 0).
                        // Compact 2-line family pattern: count accent + muted status.
                        const b = woSummary[`SO|${r.order_id}`] || woSummary[`ESO|${r.order_id}`];
                        if (!b || !b.count) return <span className="text-[11px] text-zinc-600 font-mono">No WO</span>;
                        const st = b.statuses || {};
                        const parts = Object.entries(st).map(([k, v]) => `${v} ${k}`).join(" · ");
                        return (
                          <div className="leading-tight" data-testid={`so-wo-chip-${r.order_no}`} title={b.wos.map(w => `${w.wo_no} · ${w.item_code} · ${w.status}`).join("\n")}>
                            <span className="font-mono text-xs font-semibold text-sky-300">{b.count} {b.count === 1 ? "WO" : "WOs"}</span>
                            <span className="block text-[10px] font-mono text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px]">{parts}</span>
                          </div>
                        );
                      })()}
                    </td>}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isFirst && (
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`report-actions-${r.order_no}`} className="h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap">Actions ▾</button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm min-w-[160px]">
                              <DropdownMenuItem data-testid={`report-view-${r.order_no}`} onClick={() => setPreviewOrder(r)}>View</DropdownMenuItem>
                              <DropdownMenuItem data-testid={`report-print-${r.order_no}`} onClick={() => printOne(r)}>Print / PDF</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              });
              if (isOpen && r.dispatches.length > 0) {
                out.push(
                  <tr key={`${r.order_id}-dispatches`} className="border-b border-[var(--border)] bg-[var(--bg-2)]/30">
                    <td></td>
                    <td colSpan={totalCols - 1} className="px-4 py-3">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Date-wise Dispatch Detail</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                            <th className="text-left py-1">Date</th>
                            <th className="text-left py-1">DC No</th>
                            <th className="text-left py-1">PL No(s)</th>
                            <th className="text-left py-1">Vehicle</th>
                            <th className="text-right py-1">Qty (m)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.dispatches.map(d => (
                            <tr key={d.dc_no} data-testid={`report-dispatch-${d.dc_no}`}>
                              <td className="py-1 font-mono text-zinc-400">{d.date}</td>
                              <td className="py-1 font-mono">
                                <button
                                  onClick={() => dcByNo[d.dc_no] && setPreviewDc(dcByNo[d.dc_no])}
                                  data-testid={`report-open-dc-${d.dc_no}`}
                                  title="View Delivery Challan"
                                  className="text-[var(--accent)] hover:underline focus:outline-none"
                                >
                                  {d.dc_no}
                                </button>
                              </td>
                              <td className="py-1 font-mono text-zinc-300" data-testid={`report-dispatch-pl-${d.dc_no}`}>
                                {(d.pl_nos && d.pl_nos.length > 0) ? (
                                  <span className="inline-flex flex-wrap gap-1">
                                    {d.pl_nos.map(pn => (
                                      <button
                                        key={pn}
                                        onClick={() => plByNo[pn] && setPreviewPl(plByNo[pn])}
                                        data-testid={`report-open-pl-${pn}`}
                                        title="View Packing List"
                                        className="text-[var(--accent)] hover:underline focus:outline-none"
                                      >
                                        {pn}
                                      </button>
                                    )).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, <span key={`sep-${i}`} className="text-zinc-500">, </span>, curr], [])}
                                  </span>
                                ) : "—"}
                              </td>
                              <td className="py-1 text-zinc-400">{d.vehicle_no || "—"}</td>
                              <td className="py-1 text-right font-mono">{Number(d.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                );
              }
              return out;
            })}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot className="bg-[var(--bg-2)]">
              <tr className="font-semibold">
                <td colSpan={totalCols} className="px-4 py-3">
                  <div className="flex items-center justify-end gap-6 text-xs">
                    <span className="uppercase tracking-widest text-zinc-500">Totals</span>
                    <span className="flex items-center gap-1.5"><span className="uppercase tracking-widest text-[10px] text-zinc-500">Order</span><span className="font-mono tabular-nums text-zinc-200" data-testid="report-totals-order-qty">{fmtQtyInt(totals.total)}</span></span>
                    <span className="flex items-center gap-1.5"><span className="uppercase tracking-widest text-[10px] text-zinc-500">Dispatch</span><span className="font-mono tabular-nums text-emerald-400">{fmtQtyInt(totals.disp)}</span></span>
                    <span className="flex items-center gap-1.5"><span className="uppercase tracking-widest text-[10px] text-zinc-500">Pending</span><span className="font-mono tabular-nums text-amber-400">{fmtQtyInt(totals.pending)}</span></span>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Per-order View Dialog */}
      <Dialog open={!!previewOrder} onOpenChange={(o) => !o && setPreviewOrder(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="report-view-dialog" description="Detailed status view for the selected Sales Order — dispatched vs pending metres, line-level breakdown.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>Sales Order Status — {previewOrder?.order_no}</span>
              {previewOrder && ((previewOrder.status || "").toLowerCase() === "closed" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-emerald-500/10 text-emerald-400"><LockIcon size={10} weight="bold" />CLOSED</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-amber-500/10 text-amber-400">OPEN</span>
              ))}
            </DialogTitle>
          </DialogHeader>
          {previewOrder && (
            <>
              <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
                <div>
                  <div className="overline text-[10px] mb-0.5">Customer</div>
                  <div className="font-semibold text-sm">{previewOrder.customer_name || "—"}</div>
                </div>
                <div>
                  <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                  <div className="font-mono text-sm">{previewOrder.po_number || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                <div><div className="overline text-[10px] mb-0.5">Order Date</div><div className="font-mono">{previewOrder.order_date}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Delivery Date</div><div className="font-mono">{previewOrder.delivery_date}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Date Range</div><div className="font-mono">{dateRangeLabel}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Dispatches</div><div className="font-mono">{previewOrder.dispatches.length}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Total Qty</div><div className="font-mono font-semibold">{fmtQtyInt(previewOrder.total_qty)}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Dispatched</div><div className="font-mono text-emerald-400">{fmtQtyInt(previewOrder.disp_qty)}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Pending</div><div className="font-mono text-amber-400">{fmtQtyInt(previewOrder.pending_qty)}</div></div>
                <div>
                  <div className="overline text-[10px] mb-0.5">Progress</div>
                  <div className="w-full h-2 bg-[var(--bg-2)] rounded-sm overflow-hidden mt-1">
                    <div className="h-full bg-emerald-500" style={{ width: `${previewOrder.total_qty > 0 ? Math.min(100, (previewOrder.disp_qty / previewOrder.total_qty) * 100) : 0}%` }} />
                  </div>
                </div>
              </div>

              <div className="overline mb-2">Date-wise Dispatch Detail · {previewOrder.dispatches.length}</div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--bg-2)]">
                    <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">DC No</th>
                      <th className="px-2 py-2 text-left">PL No(s)</th>
                      <th className="px-2 py-2 text-left">Vehicle</th>
                      <th className="px-2 py-2 text-right">Qty (m)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewOrder.dispatches.map(d => (
                      <tr key={d.dc_no} className="border-t border-[var(--border)]">
                        <td className="px-2 py-1 font-mono text-zinc-400">{d.date}</td>
                        <td className="px-2 py-1 font-mono">
                          <button
                            onClick={() => dcByNo[d.dc_no] && setPreviewDc(dcByNo[d.dc_no])}
                            data-testid={`report-view-open-dc-${d.dc_no}`}
                            title="View Delivery Challan"
                            className="text-[var(--accent)] hover:underline focus:outline-none"
                          >
                            {d.dc_no}
                          </button>
                        </td>
                        <td className="px-2 py-1 font-mono text-zinc-300" data-testid={`report-view-pl-${d.dc_no}`}>
                          {(d.pl_nos && d.pl_nos.length > 0) ? (
                            <span className="inline-flex flex-wrap gap-1">
                              {d.pl_nos.map(pn => (
                                <button
                                  key={pn}
                                  onClick={() => plByNo[pn] && setPreviewPl(plByNo[pn])}
                                  data-testid={`report-view-open-pl-${pn}`}
                                  title="View Packing List"
                                  className="text-[var(--accent)] hover:underline focus:outline-none"
                                >
                                  {pn}
                                </button>
                              )).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, <span key={`sep-${i}`} className="text-zinc-500">, </span>, curr], [])}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-2 py-1 text-zinc-400">{d.vehicle_no || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(d.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                    {previewOrder.dispatches.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-6 text-zinc-500">No dispatches in the selected date range.</td></tr>
                    )}
                  </tbody>
                  {previewOrder.dispatches.length > 0 && (
                    <tfoot className="bg-[var(--bg-2)]">
                      <tr className="font-semibold">
                        <td colSpan={4} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500">Total</td>
                        <td className="px-2 py-2 text-right font-mono">{previewOrder.dispatches.reduce((a, d) => a + d.quantity, 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <button onClick={() => previewOrder && printOne(previewOrder)} data-testid="report-view-print" className="px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print / PDF</button>
            <button onClick={() => setPreviewOrder(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DC Drill-down Dialog (opened by clicking a DC No in the report) */}
      <Dialog open={!!previewDc} onOpenChange={(o) => !o && setPreviewDc(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="report-dc-dialog" description="Every Delivery Challan linked to this Sales Order with dispatched quantities.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>Delivery Challan — {previewDc?.dc_no}</span>
              {previewDc?.delivered && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-emerald-500/10 text-emerald-400"><LockIcon size={10} weight="bold" />DELIVERED</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewDc && (() => {
            const ids = (previewDc.packing_list_ids && previewDc.packing_list_ids.length)
              ? previewDc.packing_list_ids
              : (previewDc.packing_list_id ? [previewDc.packing_list_id] : []);
            const linkedPls = Object.values(plByNo).filter(p => ids.includes(p.id));
            const totalBoxes = linkedPls.reduce((a, p) => a + (Number(p.no_of_packages) || 0), 0);
            return (
              <>
                <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="overline text-[10px] mb-0.5">Customer Name</div>
                    <div className="font-semibold text-sm">{previewDc.customer_name || "—"}</div>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                    <div className="font-mono text-sm">{previewDc.customer_po_no || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                  <div><div className="overline text-[10px] mb-0.5">Date</div><div className="font-mono">{previewDc.challan_date}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Vehicle No</div><div className="font-mono">{previewDc.vehicle_no || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Driver</div><div>{previewDc.driver_name || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Transporter</div><div>{previewDc.transporter || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Total Qty (m)</div><div className="font-mono font-semibold">{fmtQtyInt(previewDc.quantity)}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Total Boxes</div><div className="font-mono">{totalBoxes}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Linked PLs</div><div className="font-mono">{linkedPls.length}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Amendments</div><div className="font-mono">{(previewDc.amendments || []).length}</div></div>
                </div>
                <div className="overline mb-2">Linked Packing Lists</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--bg-2)]">
                      <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                        <th className="px-2 py-2 text-left">PL No</th>
                        <th className="px-2 py-2 text-left">Date</th>
                        <th className="px-2 py-2 text-right">Boxes</th>
                        <th className="px-2 py-2 text-right">Qty (m)</th>
                        <th className="px-2 py-2 text-right">Net Wt</th>
                        <th className="px-2 py-2 text-right">Gross Wt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedPls.map(p => (
                        <tr key={p.id} className="border-t border-[var(--border)]">
                          <td className="px-2 py-1 font-mono font-semibold">
                            <button
                              onClick={() => { setPreviewDc(null); setPreviewPl(p); }}
                              data-testid={`dc-jump-pl-${p.pl_no}`}
                              title="View Packing List"
                              className="text-[var(--accent)] hover:underline focus:outline-none"
                            >
                              {p.pl_no}
                            </button>
                          </td>
                          <td className="px-2 py-1 font-mono text-zinc-400">{p.pack_date}</td>
                          <td className="px-2 py-1 text-right font-mono">{p.no_of_packages}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(p.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(p.net_weight || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(p.gross_weight || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      {linkedPls.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-zinc-500">No packing lists linked.</td></tr>}
                    </tbody>
                  </table>
                </div>
                {previewDc.remarks && (
                  <div className="mt-3 text-xs"><span className="overline">Remarks:</span> <span className="text-zinc-400">{previewDc.remarks}</span></div>
                )}
              </>
            );
          })()}
          <DialogFooter>
            <button onClick={() => setPreviewDc(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PL Drill-down Dialog (opened by clicking a PL No in the report) */}
      <Dialog open={!!previewPl} onOpenChange={(o) => !o && setPreviewPl(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="report-pl-dialog" description="Every Packing List linked to this Sales Order with box counts and dispatched metres.">
          <DialogHeader>
            <DialogTitle className="font-display">Packing List — {previewPl?.pl_no}</DialogTitle>
          </DialogHeader>
          {previewPl && (
            <>
              <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
                <div>
                  <div className="overline text-[10px] mb-0.5">Customer Name</div>
                  <div className="font-semibold text-sm">{previewPl.customer_name || "—"}</div>
                </div>
                <div>
                  <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                  <div className="font-mono text-sm">{previewPl.customer_po_no || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                <div><div className="overline text-[10px] mb-0.5">Pack Date</div><div className="font-mono">{previewPl.pack_date}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Boxes</div><div className="font-mono">{previewPl.no_of_packages}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Qty (m)</div><div className="font-mono font-semibold">{Number(previewPl.quantity || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div></div>
                <div><div className="overline text-[10px] mb-0.5">Gross Wt</div><div className="font-mono">{Number(previewPl.gross_weight || 0).toFixed(2)}</div></div>
              </div>
              <div className="overline mb-2">Box Manifest · {(previewPl.boxes || []).length} boxes</div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[var(--bg-2)]">
                    <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                      <th className="px-2 py-2 text-left">Box No</th>
                      <th className="px-2 py-2 text-left">Product Code</th>
                      <th className="px-2 py-2 text-right">Meter</th>
                      <th className="px-2 py-2 text-right">Net</th>
                      <th className="px-2 py-2 text-right">Tare</th>
                      <th className="px-2 py-2 text-right">Gross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewPl.boxes || []).map(b => (
                      <tr key={b.box_no} className="border-t border-[var(--border)]">
                        <td className="px-2 py-1 font-mono font-semibold">{b.box_no}</td>
                        <td className="px-2 py-1 font-mono text-[11px]">{b.article_no || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(b.meter || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(b.net_wt || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(b.tare_wt || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(b.gross_wt || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {(!previewPl.boxes || previewPl.boxes.length === 0) && (
                      <tr><td colSpan={6} className="text-center py-4 text-zinc-500">No boxes recorded.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <button onClick={() => setPreviewPl(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
